import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { Plus, Trash2, FileText, Download, Send, Search, Settings, Upload, Eye, FileSpreadsheet, CheckCircle, Clock, AlertCircle, XCircle, Sparkles, Loader2, Save, FolderOpen, ClipboardCheck, PenTool, RotateCcw, ArrowRight, Edit2, Copy, Bold, Underline, Ruler, Scale } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DunningDialog } from '@/components/admin/DunningDialog';
import { FileWarning, ChevronUp, ChevronDown, Type, Sigma } from 'lucide-react';
import { SubtotalTableRow, SubtotalCard } from '@/components/admin/SubtotalBlock';

const STATUS_CONFIG = {
  offen: { label: 'Offen', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  bezahlt: { label: 'Bezahlt', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  teilbezahlt: { label: 'Teilbezahlt', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  ueberfaellig: { label: 'Überfällig', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  gemahnt: { label: 'Gemahnt', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  storniert: { label: 'Storniert', color: 'bg-gray-100 text-gray-700', icon: XCircle },
  entwurf: { label: 'Entwurf', color: 'bg-gray-100 text-gray-500', icon: FileText },
};

const MONTHS = [
  { value: 1, label: 'Januar' },
  { value: 2, label: 'Februar' },
  { value: 3, label: 'März' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'Dezember' },
];

// Textarea with formatting toolbar
const FormattedTextarea = ({ value, onChange, placeholder, className, onImprove, isImproving, 'data-testid': dataTestId }) => {
  const textareaRef = useRef(null);
  
  const applyFormatting = (format) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    if (start === end) {
      // No text selected - show hint
      toast.info('Bitte zuerst Text markieren, dann auf Formatierung klicken');
      return;
    }
    
    let wrapper = '';
    if (format === 'bold') wrapper = '**';
    if (format === 'underline') wrapper = '__';
    
    // Check if already formatted - if so, remove formatting
    const beforeSelected = value.substring(Math.max(0, start - 2), start);
    const afterSelected = value.substring(end, Math.min(value.length, end + 2));
    
    let newValue;
    if (beforeSelected === wrapper && afterSelected === wrapper) {
      // Remove formatting
      newValue = value.substring(0, start - 2) + selectedText + value.substring(end + 2);
      // Update selection
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start - 2, end - 2);
      }, 0);
    } else {
      // Add formatting
      newValue = value.substring(0, start) + wrapper + selectedText + wrapper + value.substring(end);
      // Update selection to include the new formatting
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, end + wrapper.length * 2);
      }, 0);
    }
    
    onChange({ target: { value: newValue } });
  };
  
  return (
    <div className="space-y-1">
      {/* Formatting Toolbar */}
      <div className="flex items-center gap-1 pb-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs font-bold"
          onClick={() => applyFormatting('bold')}
          title="Fett (Text markieren, dann klicken)"
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs underline"
          onClick={() => applyFormatting('underline')}
          title="Unterstrichen (Text markieren, dann klicken)"
        >
          <Underline className="h-3.5 w-3.5" />
        </Button>
        <span className="text-xs text-gray-400 ml-2">Text markieren → Button klicken</span>
        {onImprove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 ml-auto hover:bg-purple-100"
            onClick={onImprove}
            disabled={isImproving}
            title="Mit KI verbessern"
          >
            {isImproving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-purple-600" />
            ) : (
              <Sparkles className="h-3.5 w-3.5 text-purple-600" />
            )}
          </Button>
        )}
      </div>
      {/* Textarea */}
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={className}
        data-testid={dataTestId}
      />
    </div>
  );
};

export default function AdminDocumentGenerator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Per-user draft key prevents leaking drafts across accounts on the same browser
  const DRAFT_KEY = `farbwelt_document_draft_${user?.id || 'anonymous'}`;
  const [activeTab, setActiveTab] = useState('create');
  const [convertingToGutachten, setConvertingToGutachten] = useState(null);
  const [documentType, setDocumentType] = useState('rechnung');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [archive, setArchive] = useState([]);
  const [settings, setSettings] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [improvingIndex, setImprovingIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [showDraftsDialog, setShowDraftsDialog] = useState(false);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  // Send-Email dialog (with optional scheduling)
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    doc: null,
    email: '',
    schedule: false,
    scheduledAt: '',
    sending: false,
  });
  // Document Profiles (multi-profile billing)
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(() => {
    try { return localStorage.getItem('fw_active_profile') || 'default'; } catch { return 'default'; }
  });
  const [newProfileDialog, setNewProfileDialog] = useState({
    open: false,
    name: '',
    prefix_suffix: '',
    invoice_next_number: 1,
    quote_next_number: 1,
    order_next_number: 1,
    saving: false,
  });
  const [customerSearch, setCustomerSearch] = useState('');
  
  // Dunning (Mahnung) state
  const [showDunningDialog, setShowDunningDialog] = useState(false);
  const [dunningDoc, setDunningDoc] = useState(null);
  const loadedSettingsRef = useRef(null);
  
  // Aufmaß import state
  const [aufmassList, setAufmassList] = useState([]);
  const [showAufmassDialog, setShowAufmassDialog] = useState(false);
  
  // Recipient/Address Edit Dialog (edit existing archived doc)
  const [showRecipientDialog, setShowRecipientDialog] = useState(false);
  const [recipientEditDoc, setRecipientEditDoc] = useState(null);
  const [recipientForm, setRecipientForm] = useState({
    billing_name: '', billing_street: '', billing_city: '',
    delivery_name: '', delivery_street: '', delivery_city: '',
    recipient_email: '', recipient_phone: '', recipient_contact: '', recipient_vat_id: '',
    delivery_same_as_billing: false,
  });
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [regeneratingDocId, setRegeneratingDocId] = useState(null);
  
  // Document data
  const [docData, setDocData] = useState({
    documentNumber: '',
    invoiceNumber: '',
    quoteNumber: '',
    orderNumber: '',
    customerNumber: '',
    date: new Date().toLocaleDateString('de-DE'),
    deliveryDate: '',
    referenceNumber: '',
    contractNumber: '',
    introText: '',
    noteText: '',
    showTaxTable: true,
    showPaymentInfo: true,
    showQrCode: true
  });
  
  // Billing address
  const [billingAddress, setBillingAddress] = useState({
    name: '',
    street: '',
    city: '',
    country: 'Germany'
  });
  
  // Delivery address
  const [deliveryAddress, setDeliveryAddress] = useState({
    name: '',
    street: '',
    city: '',
    country: 'Germany',
    sameAsBilling: true
  });
  
  // Contact
  const [contact, setContact] = useState({
    name: '',
    phone: '',
    email: '',
    vatId: ''
  });
  
  // Positions
  const [positions, setPositions] = useState([
    { pos: 1, description: '', price: '', quantity: 1, total: '', textFormat: 'normal' }
  ]);
  
  // Tax & Payment
  const [taxRate, setTaxRate] = useState(19);
  // Reverse-Charge § 13b UStG (default = false, wird nach Settings-Load ggf. gesetzt)
  const [reverseCharge, setReverseCharge] = useState(false);
  const [payment, setPayment] = useState({
    terms: '14 Tage netto nach Rechnungserhalt',
    iban: '',
    bic: '',
    bankName: ''
  });
  
  const [footerText, setFooterText] = useState('');
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  
  // Anzahlung (Deposit) state
  const [depositAmount, setDepositAmount] = useState('');
  const [depositMode, setDepositMode] = useState('eur'); // 'eur' or 'percent'
  const [depositPercent, setDepositPercent] = useState('');
  const [referenceQuoteNumber, setReferenceQuoteNumber] = useState('');
  
  // Templates state
  const [textTemplates, setTextTemplates] = useState([]);
  const [positionTemplates, setPositionTemplates] = useState([]);
  const [showPositionTemplates, setShowPositionTemplates] = useState(false);
  
  // Load templates
  const loadTemplates = async () => {
    try {
      const [textRes, posRes] = await Promise.all([
        api.get('/admin/documents/text-templates'),
        api.get('/admin/documents/position-templates')
      ]);
      setTextTemplates(textRes.data || []);
      setPositionTemplates(posRes.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };
  
  // Load Document Profiles
  const loadProfiles = async () => {
    try {
      const res = await api.get('/admin/document-profiles');
      setProfiles(res.data || []);
    } catch (e) {
      console.error('loadProfiles failed:', e);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadCustomers();
    loadProfiles();
    loadSettings();
    loadArchive();
    loadTemplates();
  }, []);

  // Reload profile-scoped data when active profile changes
  useEffect(() => {
    try { localStorage.setItem('fw_active_profile', activeProfile); } catch {}
    loadArchive();
    loadNextNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile]);

  // Reload archive when type/status filter or search term changes
  useEffect(() => {
    const t = setTimeout(() => loadArchive(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterStatus, searchTerm]);

  // Load Aufmaße for import
  const loadAufmassForImport = async () => {
    try {
      const res = await api.get('/admin/aufmass');
      setAufmassList(res.data || []);
      setShowAufmassDialog(true);
    } catch (e) {
      toast.error('Aufmaße konnten nicht geladen werden');
    }
  };

  // Import data from Aufmaß into document
  const importFromAufmass = (aufmass) => {
    // Set customer data
    if (aufmass.customer_name) {
      setBillingAddress({
        name: aufmass.customer_name || '',
        street: (aufmass.customer_address || '').split(',')[0]?.trim() || '',
        city: (aufmass.customer_address || '').split(',').slice(1).join(',').trim() || '',
        country: 'Deutschland'
      });
      setContact({
        name: aufmass.customer_name || '',
        phone: aufmass.customer_phone || '',
        email: aufmass.customer_email || '',
        vatId: ''
      });
    }
    // If customer_id exists, select the customer
    if (aufmass.customer_id) {
      const customer = customers.find(c => c.id === aufmass.customer_id);
      if (customer) setSelectedCustomer(customer);
    }

    // Build positions from Aufmaß data
    const newPositions = [];
    let posNum = 1;

    // Add wall work positions
    const walls = aufmass.walls || [];
    const arbeiten = aufmass.arbeiten || [];
    
    if (walls.length > 0 && arbeiten.length > 0) {
      // Calculate total wall area
      const totalArea = walls.reduce((sum, w) => {
        const b = parseFloat(w.breite) || 0;
        const h = parseFloat(w.hoehe) || 0;
        return sum + (b * h);
      }, 0);

      // Create one position per work type
      for (const arbeit of arbeiten) {
        const roomList = [...new Set(walls.map(w => w.raum).filter(Boolean))].join(', ');
        newPositions.push({
          pos: posNum++,
          description: `${arbeit}${roomList ? ` (${roomList})` : ''} - ${totalArea.toFixed(2)} m²`,
          price: '',
          quantity: 1,
          total: '',
          textFormat: 'normal',
          is_optional: false
        });
      }
    }

    // Add ceiling if specified
    if (aufmass.decke_m2 && arbeiten.some(a => a.toLowerCase().includes('decke'))) {
      // Already included above
    } else if (aufmass.decke_m2) {
      newPositions.push({
        pos: posNum++,
        description: `Decke streichen - ${aufmass.decke_m2} m²`,
        price: '',
        quantity: 1,
        total: '',
        textFormat: 'normal',
        is_optional: false
      });
    }

    // Add doors if specified
    if (aufmass.tueren_anzahl && parseInt(aufmass.tueren_anzahl) > 0) {
      const hasLackieren = arbeiten.some(a => a.toLowerCase().includes('lackieren') && a.toLowerCase().includes('tür'));
      if (!hasLackieren) {
        newPositions.push({
          pos: posNum++,
          description: `Türen lackieren - ${aufmass.tueren_anzahl} Stück`,
          price: '',
          quantity: parseInt(aufmass.tueren_anzahl),
          total: '',
          textFormat: 'normal',
          is_optional: false
        });
      }
    }

    // Add material as optional position
    if ((aufmass.material || []).length > 0) {
      newPositions.push({
        pos: posNum++,
        description: `Material: ${aufmass.material.join(', ')}`,
        price: '',
        quantity: 1,
        total: '',
        textFormat: 'light',
        is_optional: true
      });
    }

    // Add notes as info position
    if (aufmass.besonderheiten) {
      newPositions.push({
        pos: posNum++,
        description: `Hinweis: ${aufmass.besonderheiten}`,
        price: '',
        quantity: 1,
        total: '',
        textFormat: 'light',
        is_optional: true
      });
    }

    if (newPositions.length > 0) {
      setPositions(newPositions);
    }

    // Set color info in footer or reference
    if (aufmass.farbton) {
      setFooterText(prev => prev ? `${prev}\nFarbton: ${aufmass.farbton}` : `Farbton: ${aufmass.farbton}`);
    }

    setShowAufmassDialog(false);
    setActiveTab('create');
    toast.success(`Aufmaß von ${aufmass.customer_name || 'Kunde'} übernommen`);
  };
  
  // Auto-save draft when leaving page (if there are positions with content)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasContent = positions.some(p => p.description || p.price);
      if (hasContent && activeTab === 'create') {
        // Trigger auto-save
        const autoSaveData = {
          document_type: documentType,
          customer_number: selectedCustomer?.customer_number || '',
          billing_address: billingAddress,
          delivery_address: deliveryAddress,
          contact: contact,
          positions: positions,
          doc_data: docData,
          tax_rate: taxRate,
          payment: payment,
          footer_text: footerText,
          auto_saved: true,
          saved_at: new Date().toISOString()
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(autoSaveData));
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [positions, documentType, selectedCustomer, billingAddress, deliveryAddress, contact, docData, taxRate, payment, footerText, activeTab]);
  
  // Continuous auto-save every 30 seconds when there's content
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      const hasContent = positions.some(p => p.description || p.price) || billingAddress.name;
      if (hasContent && activeTab === 'create') {
        const autoSaveData = {
          document_type: documentType,
          customer_number: selectedCustomer?.customer_number || '',
          customer_id: selectedCustomer?.id || '',
          billing_address: billingAddress,
          delivery_address: deliveryAddress,
          contact: contact,
          positions: positions,
          doc_data: docData,
          tax_rate: taxRate,
          payment: payment,
          footer_text: footerText,
          auto_saved: true,
          saved_at: new Date().toISOString()
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(autoSaveData));
        console.log('Document auto-saved to localStorage');
      }
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(autoSaveInterval);
  }, [positions, documentType, selectedCustomer, billingAddress, deliveryAddress, contact, docData, taxRate, payment, footerText, activeTab]);
  
  // Restore auto-saved draft on load
  useEffect(() => {
    // Cleanup legacy unscoped draft key (pre multi-tenancy) so it never leaks across accounts
    try { localStorage.removeItem('farbwelt_document_draft'); } catch (e) {}
    const savedDraft = localStorage.getItem(DRAFT_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        const savedTime = new Date(draft.saved_at);
        const hoursSinceLastSave = (Date.now() - savedTime.getTime()) / (1000 * 60 * 60);
        
        // Only restore if saved within last 48 hours and has content
        if (hoursSinceLastSave < 48 && (draft.positions?.length > 0 || draft.billing_address?.name)) {
          toast.info(
            <div>
              <p className="font-medium">Nicht gespeichertes Dokument gefunden!</p>
              <p className="text-sm text-gray-600">
                Vom {savedTime.toLocaleDateString('de-DE')} um {savedTime.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'})}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Typ: {draft.document_type === 'rechnung' ? 'Rechnung' : draft.document_type === 'angebot' ? 'Angebot' : draft.document_type === 'abschlag' ? 'Abschlagsrechnung' : draft.document_type === 'auftragsbestaetigung' ? 'Auftragsbestätigung' : draft.document_type}
                {draft.billing_address?.name && ` • ${draft.billing_address.name}`}
              </p>
              <div className="flex gap-2 mt-3">
                <button 
                  onClick={() => {
                    if (draft.document_type) setDocumentType(draft.document_type);
                    if (draft.billing_address) setBillingAddress(draft.billing_address);
                    if (draft.delivery_address) setDeliveryAddress(draft.delivery_address);
                    if (draft.contact) setContact(draft.contact);
                    if (draft.positions?.length) setPositions(draft.positions);
                    if (draft.doc_data) setDocData(prev => ({ ...prev, ...draft.doc_data }));
                    if (draft.tax_rate) setTaxRate(draft.tax_rate);
                    if (draft.payment) setPayment(draft.payment);
                    if (draft.footer_text) setFooterText(draft.footer_text);
                    // Try to restore customer selection
                    if (draft.customer_id && customers.length > 0) {
                      const customer = customers.find(c => c.id === draft.customer_id);
                      if (customer) setSelectedCustomer(customer);
                    }
                    localStorage.removeItem(DRAFT_KEY);
                    toast.success('Dokument wiederhergestellt');
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-sm font-medium"
                >
                  Wiederherstellen
                </button>
                <button 
                  onClick={() => {
                    localStorage.removeItem(DRAFT_KEY);
                    toast.info('Auto-Save verworfen');
                  }}
                  className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1.5 rounded text-sm font-medium"
                >
                  Verwerfen
                </button>
              </div>
            </div>,
            { duration: 20000 }
          );
        } else {
          // Old draft, remove it
          localStorage.removeItem(DRAFT_KEY);
        }
      } catch (e) {
        localStorage.removeItem(DRAFT_KEY);
      }
    }
  }, []);
  
  // Load next number when document type changes
  useEffect(() => {
    loadNextNumber();
  }, [documentType]);
  
  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };
  
  const loadSettings = async () => {
    try {
      const response = await api.get('/admin/documents/settings');
      setSettings(response.data);
      loadedSettingsRef.current = response.data;
      // Reverse-Charge Default aus Settings übernehmen (falls Nutzer nicht bereits umgeschaltet hat)
      if (response.data.reverse_charge === true) {
        setReverseCharge(true);
      }
      // Set default payment info from settings
      setPayment(prev => ({
        ...prev,
        terms: response.data.default_payment_terms || prev.terms,
        iban: response.data.default_iban || prev.iban,
        bic: response.data.default_bic || prev.bic,
        bankName: response.data.default_bank_name || prev.bankName
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };
  
  // Filtered customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const search = customerSearch.toLowerCase();
    return customers.filter(c => 
      c.first_name?.toLowerCase().includes(search) ||
      c.last_name?.toLowerCase().includes(search) ||
      c.customer_number?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.phone?.toLowerCase().includes(search) ||
      c.city?.toLowerCase().includes(search)
    );
  }, [customers, customerSearch]);

  const loadNextNumber = async () => {
    try {
      const response = await api.get(`/admin/documents/next-number/${documentType}?profile_id=${encodeURIComponent(activeProfile)}`);
      const numField = {
        'rechnung': 'invoiceNumber',
        'angebot': 'quoteNumber',
        'auftragsbestaetigung': 'orderNumber',
        'abschlag': 'abschlagNumber'
      }[documentType];
      setDocData(prev => ({
        ...prev,
        documentNumber: response.data.document_number,
        [numField]: response.data.document_number
      }));
    } catch (error) {
      console.error('Failed to load next number:', error);
    }
  };
  
  const loadArchive = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('doc_type', filterType);
      if (searchTerm) params.append('search', searchTerm);
      params.append('profile_id', activeProfile);
      const response = await api.get(`/admin/documents/archive?${params}`);
      // Filter by status locally if needed
      let docs = response.data;
      if (filterStatus !== 'all') {
        docs = docs.filter(d => d.status === filterStatus);
      }
      setArchive(docs);
    } catch (error) {
      console.error('Failed to load archive:', error);
    }
  };
  
  const loadMonthlyStats = async () => {
    try {
      const response = await api.get(`/admin/documents/stats/monthly?year=${exportYear}`);
      setMonthlyStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };
  
  // Update status
  const updateDocumentStatus = async (docId, newStatus) => {
    try {
      await api.put(`/admin/documents/${docId}/status`, { status: newStatus });
      toast.success(`Status auf "${STATUS_CONFIG[newStatus]?.label}" geändert`);
      loadArchive();
    } catch (error) {
      toast.error('Status konnte nicht geändert werden');
    }
  };
  
  // DATEV Export
  const exportDATEV = async () => {
    try {
      const response = await api.get(`/admin/documents/export/datev?month=${exportMonth}&year=${exportYear}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DATEV_Export_${MONTHS[exportMonth-1]?.label}_${exportYear}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('DATEV-Export erstellt');
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };
  
  // Select customer from list
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setDocData(prev => ({
      ...prev,
      customerNumber: customer.customer_number
    }));
    setBillingAddress({
      name: `${customer.first_name} ${customer.last_name}`.trim(),
      street: customer.street || '',
      city: `${customer.postal_code || ''} ${customer.city || ''}`.trim(),
      country: 'Germany'
    });
    if (deliveryAddress.sameAsBilling) {
      setDeliveryAddress(prev => ({
        ...prev,
        name: `${customer.first_name} ${customer.last_name}`.trim(),
        street: customer.street || '',
        city: `${customer.postal_code || ''} ${customer.city || ''}`.trim()
      }));
    }
    setContact({
      name: `${customer.first_name} ${customer.last_name}`.trim(),
      phone: customer.phone || '',
      email: customer.email || '',
      vatId: ''
    });
  };
  
  // Positions management
  // Freitext-Blöcke bekommen keine Positionsnummer
  const renumber = (list) => {
    let n = 0;
    return list.map((p) => (p.is_text || p.is_subtotal ? { ...p, pos: '' } : { ...p, pos: ++n }));
  };

  const addPosition = () => {
    setPositions(renumber([...positions, { description: '', price: '', quantity: 1, total: '', textFormat: 'normal' }]));
  };

  const addTextBlock = () => {
    setPositions(renumber([...positions, { description: '', price: '', quantity: 1, total: '', textFormat: 'normal', is_text: true }]));
  };

  const addSubtotal = () => {
    setPositions(renumber([...positions, { description: 'Zwischensumme', price: '', quantity: 1, total: '', is_subtotal: true }]));
  };

  const movePosition = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= positions.length) return;
    const list = [...positions];
    [list[index], list[target]] = [list[target], list[index]];
    setPositions(renumber(list));
  };
  
  const removePosition = (index) => {
    setPositions(renumber(positions.filter((_, i) => i !== index)));
  };
  
  const updatePosition = (index, field, value) => {
    const newPositions = [...positions];
    newPositions[index][field] = value;
    setPositions(newPositions);
  };

  // KI Textverbesserung für Beschreibung
  const improveDescription = async (index) => {
    const text = positions[index].description;
    if (!text || text.trim().length < 3) {
      toast.error('Bitte erst einen Text eingeben');
      return;
    }
    
    setImprovingIndex(index);
    try {
      const response = await api.post('/admin/documents/improve-text', { text });
      if (response.data.improved_text) {
        updatePosition(index, 'description', response.data.improved_text);
        toast.success('Text verbessert');
      }
    } catch (error) {
      console.error('AI improvement failed:', error);
      toast.error(error.response?.data?.detail || 'KI-Verbesserung fehlgeschlagen');
    } finally {
      setImprovingIndex(null);
    }
  };

  // Zwischenspeichern - Entwurf speichern
  const saveDraft = async () => {
    if (!billingAddress.name) {
      toast.error('Bitte Kundenname ausfüllen');
      return;
    }
    
    setIsSaving(true);
    try {
      const deliveryAddr = deliveryAddress.sameAsBilling ? billingAddress : deliveryAddress;
      
      const draftData = {
        document_type: documentType,
        customer_id: selectedCustomer?.id,
        customer_number: docData.customerNumber,
        billing_name: billingAddress.name,
        billing_street: billingAddress.street,
        billing_city: billingAddress.city,
        billing_country: billingAddress.country,
        delivery_name: deliveryAddr.name,
        delivery_street: deliveryAddr.street,
        delivery_city: deliveryAddr.city,
        delivery_country: deliveryAddr.country,
        recipient_contact: contact.name,
        recipient_phone: contact.phone,
        recipient_email: contact.email,
        recipient_vat_id: contact.vatId,
        document_number: docData.documentNumber,
        invoice_number: docData.invoiceNumber,
        quote_number: docData.quoteNumber,
        order_confirmation_number: docData.orderNumber,
        document_date: docData.date,
        delivery_date: docData.deliveryDate,
        reference_number: docData.referenceNumber,
        contract_number: docData.contractNumber,
        positions: positions.map(p => ({
          pos: p.pos,
          description: p.description,
          price: p.price ? parseFloat(p.price) : null,
          quantity: parseInt(p.quantity) || 1,
          total: p.total ? parseFloat(p.total) : null,
          text_format: p.textFormat || 'normal',
          is_text: !!p.is_text,
          is_subtotal: !!p.is_subtotal
        })),
        tax_rate: taxRate,
        payment_terms: payment.terms,
        bank_name: payment.bankName,
        iban: payment.iban,
        bic: payment.bic,
        footer_text: footerText,
        layout: settings.selected_layout || 1,
        status: 'entwurf',
        draft_id: currentDraftId
      };
      
      const response = await api.post('/admin/documents/save-draft', draftData);
      setCurrentDraftId(response.data.draft_id);
      toast.success('Entwurf gespeichert');
    } catch (error) {
      console.error('Save draft failed:', error);
      toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  // Entwürfe laden
  const loadDrafts = async () => {
    setIsLoadingDrafts(true);
    try {
      const response = await api.get('/admin/documents/drafts');
      setDrafts(response.data);
      setShowDraftsDialog(true);
    } catch (error) {
      console.error('Failed to load drafts:', error);
      toast.error('Entwürfe konnten nicht geladen werden');
    } finally {
      setIsLoadingDrafts(false);
    }
  };

  // Entwurf in Formular laden
  const loadDraftIntoForm = (draft) => {
    // Dokumenttyp setzen
    setDocumentType(draft.document_type || 'rechnung');
    
    // Dokument-Daten
    setDocData({
      documentNumber: draft.document_number || '',
      invoiceNumber: draft.invoice_number || '',
      quoteNumber: draft.quote_number || '',
      orderNumber: draft.order_confirmation_number || '',
      customerNumber: draft.customer_number || '',
      date: draft.document_date || new Date().toLocaleDateString('de-DE'),
      deliveryDate: draft.delivery_date || '',
      referenceNumber: draft.reference_number || '',
      contractNumber: draft.contract_number || ''
    });
    
    // Rechnungsadresse
    setBillingAddress({
      name: draft.billing_name || '',
      street: draft.billing_street || '',
      city: draft.billing_city || '',
      country: draft.billing_country || 'Germany'
    });
    
    // Lieferadresse
    const sameAddr = draft.billing_name === draft.delivery_name && 
                     draft.billing_street === draft.delivery_street;
    setDeliveryAddress({
      name: draft.delivery_name || '',
      street: draft.delivery_street || '',
      city: draft.delivery_city || '',
      country: draft.delivery_country || 'Germany',
      sameAsBilling: sameAddr
    });
    
    // Kontakt
    setContact({
      name: draft.recipient_contact || '',
      phone: draft.recipient_phone || '',
      email: draft.recipient_email || '',
      vatId: draft.recipient_vat_id || ''
    });
    
    // Positionen
    if (draft.positions && draft.positions.length > 0) {
      setPositions(draft.positions.map((p, idx) => ({
        pos: p.pos || idx + 1,
        description: p.description || '',
        price: p.price ? String(p.price) : '',
        quantity: p.quantity || 1,
        total: p.total ? String(p.total) : '',
        textFormat: p.text_format || 'normal',
        is_text: !!p.is_text,
        is_subtotal: !!p.is_subtotal
      })));
    }
    
    // Steuer & Zahlung
    setTaxRate(draft.tax_rate || 19);
    setPayment({
      terms: draft.payment_terms || '14 Tage netto nach Rechnungserhalt',
      iban: draft.iban || '',
      bic: draft.bic || '',
      bankName: draft.bank_name || ''
    });
    
    // Footer
    setFooterText(draft.footer_text || '');
    
    // Draft ID speichern für späteres Überschreiben
    setCurrentDraftId(draft.id);
    
    // Kunde auswählen falls vorhanden
    if (draft.customer_id) {
      const customer = customers.find(c => c.id === draft.customer_id);
      if (customer) setSelectedCustomer(customer);
    }
    
    setShowDraftsDialog(false);
    toast.success('Entwurf geladen');
  };

  // Entwurf löschen
  const deleteDraft = async (draftId) => {
    try {
      await api.delete(`/admin/documents/drafts/${draftId}`);
      setDrafts(drafts.filter(d => d.id !== draftId));
      if (currentDraftId === draftId) setCurrentDraftId(null);
      toast.success('Entwurf gelöscht');
    } catch (error) {
      console.error('Delete draft failed:', error);
      toast.error('Löschen fehlgeschlagen');
    }
  };

  // ============ ABNAHMEPROTOKOLL FUNCTIONS ============
  
  // Kunde für Protokoll auswählen
  const selectProtocolCustomer = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setProtocolCustomer(customer);
      // Projektadresse mit Kundenadresse vorausfüllen
      setProtocolData(prev => ({
        ...prev,
        projectAddress: customer.street || '',
        projectCity: `${customer.postal_code || ''} ${customer.city || ''}`.trim(),
      }));
    }
  };

  // Unterschrift Canvas initialisieren
  const initSignatureCanvas = (canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return null;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    let isDrawing = false;
    let lastX = 0;
    let lastY = 0;
    
    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      if (e.touches) {
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY
        };
      }
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    };
    
    const startDrawing = (e) => {
      e.preventDefault();
      isDrawing = true;
      const pos = getPos(e);
      lastX = pos.x;
      lastY = pos.y;
    };
    
    const draw = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastX = pos.x;
      lastY = pos.y;
    };
    
    const stopDrawing = () => {
      isDrawing = false;
    };
    
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);
    canvas.addEventListener('touchstart', startDrawing);
    canvas.addEventListener('touchmove', draw);
    canvas.addEventListener('touchend', stopDrawing);
    
    return canvas;
  };

  // Unterschrift speichern
  const saveSignature = (canvasId, type) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    
    const dataUrl = canvas.toDataURL('image/png');
    if (type === 'customer') {
      setProtocolData(prev => ({ ...prev, customerSignature: dataUrl }));
      setIsSigningCustomer(false);
      toast.success('Kundenunterschrift gespeichert');
    } else {
      setProtocolData(prev => ({ ...prev, contractorSignature: dataUrl }));
      setIsSigningContractor(false);
      toast.success('Auftragnehmerunterschrift gespeichert');
    }
  };

  // Unterschrift löschen
  const clearSignature = (canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Protokoll Vorschau
  const previewProtocol = async () => {
    if (!protocolCustomer) {
      toast.error('Bitte wählen Sie einen Kunden aus');
      return;
    }
    
    setIsPreviewingProtocol(true);
    try {
      const response = await api.post('/admin/protocols/preview', {
        customer_id: protocolCustomer.id,
        customer_name: `${protocolCustomer.first_name} ${protocolCustomer.last_name}`,
        customer_address: `${protocolCustomer.street}, ${protocolCustomer.postal_code} ${protocolCustomer.city}`,
        project_name: protocolData.projectName,
        project_address: protocolData.projectAddress,
        project_city: protocolData.projectCity,
        work_description: protocolData.workDescription,
        acceptance_date: protocolData.acceptanceDate,
        acceptance_type: protocolData.acceptanceType,
        defects: protocolData.defects,
        notes: protocolData.notes,
        customer_signature: protocolData.customerSignature || '',
        contractor_signature: protocolData.contractorSignature || '',
      });
      
      setProtocolPreviewHtml(response.data);
      setShowProtocolPreview(true);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error('Vorschau fehlgeschlagen');
    } finally {
      setIsPreviewingProtocol(false);
    }
  };

  // Protokoll speichern
  const saveProtocol = async () => {
    if (!protocolCustomer) {
      toast.error('Bitte wählen Sie einen Kunden aus');
      return;
    }
    if (!protocolData.customerSignature) {
      toast.error('Bitte lassen Sie den Kunden unterschreiben');
      return;
    }
    
    setIsSavingProtocol(true);
    try {
      const response = await api.post('/admin/protocols/save', {
        customer_id: protocolCustomer.id,
        customer_name: `${protocolCustomer.first_name} ${protocolCustomer.last_name}`,
        customer_address: `${protocolCustomer.street}, ${protocolCustomer.postal_code} ${protocolCustomer.city}`,
        project_name: protocolData.projectName,
        project_address: protocolData.projectAddress,
        project_city: protocolData.projectCity,
        work_description: protocolData.workDescription,
        acceptance_date: protocolData.acceptanceDate,
        acceptance_type: protocolData.acceptanceType,
        defects: protocolData.defects,
        notes: protocolData.notes,
        customer_signature: protocolData.customerSignature,
        contractor_signature: protocolData.contractorSignature,
      });
      
      toast.success('Abnahmeprotokoll gespeichert');
      // Reset form
      setProtocolData({
        projectName: '',
        projectAddress: '',
        projectCity: '',
        workDescription: '',
        acceptanceDate: new Date().toLocaleDateString('de-DE'),
        acceptanceType: 'ohne_vorbehalt',
        defects: '',
        notes: '',
        customerSignature: null,
        contractorSignature: null,
      });
      setProtocolCustomer(null);
      loadSavedProtocols();
    } catch (error) {
      console.error('Save protocol failed:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSavingProtocol(false);
    }
  };

  // Protokolle laden
  const loadSavedProtocols = async () => {
    try {
      const response = await api.get('/admin/protocols/list');
      setSavedProtocols(response.data);
    } catch (error) {
      console.error('Load protocols failed:', error);
    }
  };

  // Protokoll als PDF herunterladen
  const downloadProtocolPdf = async (protocolId) => {
    try {
      const response = await api.get(`/admin/protocols/${protocolId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Abnahmeprotokoll_${protocolId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download fehlgeschlagen');
    }
  };

  // Protokoll per Mail senden
  const sendProtocolEmail = async (protocolId) => {
    try {
      await api.post(`/admin/protocols/${protocolId}/send-email`);
      toast.success('Protokoll per E-Mail gesendet');
    } catch (error) {
      console.error('Send email failed:', error);
      toast.error('E-Mail-Versand fehlgeschlagen');
    }
  };

  // Protokolle beim Start laden
  useEffect(() => {
    if (activeTab === 'protocol') {
      loadSavedProtocols();
    }
  }, [activeTab]);
  
  // ============ END ABNAHMEPROTOKOLL FUNCTIONS ============
  
  // Calculate totals (excluding optional positions, text blocks and subtotals)
  const lineNet = (p) => {
    if (p.is_optional || p.is_text || p.is_subtotal) return 0;
    const total = p.total ? parseFloat(p.total) : (parseFloat(p.price) || 0) * (parseInt(p.quantity) || 1);
    return isNaN(total) ? 0 : total;
  };
  const netTotal = positions.reduce((sum, p) => sum + lineNet(p), 0);
  const subtotalAt = (index) => {
    const prev = positions.slice(0, index).map((p) => !!p.is_subtotal).lastIndexOf(true);
    const net = positions.slice(prev + 1, index).reduce((sum, p) => sum + lineNet(p), 0);
    return { net, gross: reverseCharge ? net : net * (1 + taxRate / 100) };
  };
  
  // Discount calculation
  const discountAmount = discountInfo ? (
    discountInfo.type === 'percentage' 
      ? netTotal * (discountInfo.value / 100) 
      : Math.min(discountInfo.value, netTotal)
  ) : 0;
  const netAfterDiscount = netTotal - discountAmount;
  const taxAmount = reverseCharge ? 0 : netAfterDiscount * (taxRate / 100);
  const grossTotal = netAfterDiscount + taxAmount;
  
  // Validate discount code
  const validateDiscount = async () => {
    if (!discountCode.trim()) { setDiscountInfo(null); return; }
    setIsValidatingDiscount(true);
    try {
      const res = await api.post('/admin/discount-codes/validate', { code: discountCode, order_total: netTotal });
      setDiscountInfo(res.data);
      toast.success(`Rabattcode "${res.data.code}" gültig: ${res.data.type === 'percentage' ? res.data.value + '%' : res.data.discount_amount.toFixed(2) + ' EUR'}`);
    } catch (err) {
      setDiscountInfo(null);
      toast.error(err.response?.data?.detail || 'Ungültiger Rabattcode');
    } finally {
      setIsValidatingDiscount(false);
    }
  };
  
  const removeDiscount = () => { setDiscountCode(''); setDiscountInfo(null); };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };
  
  // Generate PDF
  const generatePDF = async () => {
    if (!billingAddress.name) {
      toast.error('Bitte Kundenname ausfüllen');
      return;
    }

    // Validate every position has a description + a resolvable amount
    const invalidPositions = [];
    positions.forEach((p, idx) => {
      if (p.is_subtotal) return;
      if (p.is_text) {
        if (!(p.description || '').trim()) invalidPositions.push({ pos: `Freitext ${idx + 1}`, missingDesc: true, missingAmount: false });
        return;
      }
      const priceNum = parseFloat(p.price);
      const totalNum = parseFloat(p.total);
      const hasAmount = (!Number.isNaN(priceNum) && priceNum > 0) || (!Number.isNaN(totalNum) && totalNum > 0);
      const hasDesc = (p.description || '').trim().length > 0;
      if (!hasDesc || !hasAmount) {
        invalidPositions.push({ pos: p.pos || idx + 1, missingDesc: !hasDesc, missingAmount: !hasAmount });
      }
    });
    if (invalidPositions.length > 0) {
      const first = invalidPositions[0];
      const reason = [];
      if (first.missingDesc) reason.push('Beschreibung');
      if (first.missingAmount) reason.push('Betrag');
      toast.error(
        `${String(first.pos).startsWith('Freitext') ? first.pos : `Position ${first.pos}`}: bitte ${reason.join(' und ')} eingeben. ` +
        `(${invalidPositions.length} unvollständige Position${invalidPositions.length === 1 ? '' : 'en'})`
      );
      return;
    }

    setIsGenerating(true);
    
    try {
      const deliveryAddr = deliveryAddress.sameAsBilling ? billingAddress : deliveryAddress;
      
      // Map document number to correct field based on document type
      const docNumberField = {
        rechnung: { invoice_number: docData.documentNumber },
        angebot: { quote_number: docData.documentNumber },
        auftragsbestaetigung: { order_confirmation_number: docData.documentNumber },
        abschlag: { abschlag_number: docData.documentNumber }
      };
      
      const response = await api.post('/admin/documents/generate-pdf', {
        document_type: documentType,
        customer_id: selectedCustomer?.id,
        customer_number: docData.customerNumber,
        billing_name: billingAddress.name,
        billing_street: billingAddress.street,
        billing_city: billingAddress.city,
        billing_country: billingAddress.country,
        delivery_name: deliveryAddr.name,
        delivery_street: deliveryAddr.street,
        delivery_city: deliveryAddr.city,
        delivery_country: deliveryAddr.country,
        recipient_contact: contact.name,
        recipient_phone: contact.phone,
        recipient_email: contact.email,
        recipient_vat_id: contact.vatId,
        document_number: docData.documentNumber,
        ...docNumberField[documentType],
        document_date: docData.date,
        delivery_date: docData.deliveryDate,
        reference_number: docData.referenceNumber,
        contract_number: docData.contractNumber,
        intro_text: docData.introText || '',
        note_text: docData.noteText || '',
        show_tax_table: docData.showTaxTable !== false,
        show_payment_info: docData.showPaymentInfo !== false,
        show_qr_code: docData.showQrCode !== false,
        reverse_charge: reverseCharge,
        positions: positions.map(p => ({
          pos: p.pos,
          description: p.description,
          price: p.price ? parseFloat(p.price) : null,
          quantity: parseInt(p.quantity) || 1,
          total: p.total ? parseFloat(p.total) : null,
          text_format: p.textFormat || 'normal',
          is_text: !!p.is_text,
          is_subtotal: !!p.is_subtotal
        })),
        tax_rate: taxRate,
        payment_terms: payment.terms,
        bank_name: payment.bankName,
        iban: payment.iban,
        bic: payment.bic,
        footer_text: footerText,
        save_document: true,
        layout: settings.selected_layout || 1,
        discount_code: discountInfo?.code || null,
        discount_type: discountInfo?.type || null,
        discount_value: discountInfo?.value || null,
        discount_code_id: discountInfo?.id || null,
        deposit_amount: depositMode === 'eur' ? (parseFloat(depositAmount) || null) : null,
        deposit_percentage: depositMode === 'percent' ? (parseFloat(depositPercent) || null) : null,
        reference_quote_number: referenceQuoteNumber || null,
        profile_id: activeProfile,
      }, {
        responseType: 'blob'
      });
      
      // Download PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const typeNames = { rechnung: 'Rechnung', angebot: 'Angebot', auftragsbestaetigung: 'Auftragsbestaetigung', abschlag: 'Abschlagsrechnung' };
      link.download = `${typeNames[documentType]}_${docData.documentNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF erstellt und gespeichert');
      loadArchive();
      loadNextNumber();
    } catch (error) {
      console.error('PDF generation failed:', error);
      // The response is a Blob (responseType='blob'), so error.response.data is a Blob.
      // Read it out to reveal the real backend error message.
      let detail = null;
      try {
        const respData = error?.response?.data;
        if (respData instanceof Blob) {
          const text = await respData.text();
          try {
            const parsed = JSON.parse(text);
            detail = parsed?.detail || parsed?.message || text;
          } catch {
            detail = text;
          }
        } else if (typeof respData === 'string') {
          try {
            detail = JSON.parse(respData)?.detail || respData;
          } catch { detail = respData; }
        } else if (respData?.detail) {
          detail = respData.detail;
        }
      } catch { /* ignore */ }
      toast.error(
        detail ? `PDF-Generierung fehlgeschlagen: ${detail}` : 'PDF-Generierung fehlgeschlagen',
        { duration: 8000 }
      );
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Preview document
  const previewDocument = async () => {
    if (!billingAddress.name) {
      toast.error('Bitte Kundenname ausfüllen');
      return;
    }
    
    setIsPreviewing(true);
    
    try {
      const deliveryAddr = deliveryAddress.sameAsBilling ? billingAddress : deliveryAddress;
      
      // Map document number to correct field based on document type
      const docNumberField = {
        rechnung: { invoice_number: docData.documentNumber },
        angebot: { quote_number: docData.documentNumber },
        auftragsbestaetigung: { order_confirmation_number: docData.documentNumber },
        abschlag: { abschlag_number: docData.documentNumber }
      };
      
      const response = await api.post('/admin/documents/preview-html', {
        document_type: documentType,
        customer_id: selectedCustomer?.id,
        customer_number: docData.customerNumber,
        billing_name: billingAddress.name,
        billing_street: billingAddress.street,
        billing_city: billingAddress.city,
        billing_country: billingAddress.country,
        delivery_name: deliveryAddr.name,
        delivery_street: deliveryAddr.street,
        delivery_city: deliveryAddr.city,
        delivery_country: deliveryAddr.country,
        recipient_contact: contact.name,
        recipient_phone: contact.phone,
        recipient_email: contact.email,
        recipient_vat_id: contact.vatId,
        document_number: docData.documentNumber,
        ...docNumberField[documentType],
        document_date: docData.date,
        delivery_date: docData.deliveryDate,
        reference_number: docData.referenceNumber,
        contract_number: docData.contractNumber,
        intro_text: docData.introText || '',
        note_text: docData.noteText || '',
        show_tax_table: docData.showTaxTable !== false,
        show_payment_info: docData.showPaymentInfo !== false,
        show_qr_code: docData.showQrCode !== false,
        reverse_charge: reverseCharge,
        positions: positions.map(p => ({
          pos: p.pos,
          description: p.description,
          price: p.price ? parseFloat(p.price) : null,
          quantity: parseInt(p.quantity) || 1,
          total: p.total ? parseFloat(p.total) : null,
          text_format: p.textFormat || 'normal',
          is_text: !!p.is_text,
          is_subtotal: !!p.is_subtotal
        })),
        tax_rate: taxRate,
        payment_terms: payment.terms,
        bank_name: payment.bankName,
        iban: payment.iban,
        bic: payment.bic,
        footer_text: footerText,
        save_document: false,
        layout: settings.selected_layout || 1,
        discount_code: discountInfo?.code || null,
        discount_type: discountInfo?.type || null,
        discount_value: discountInfo?.value || null,
        discount_code_id: discountInfo?.id || null,
        deposit_amount: depositMode === 'eur' ? (parseFloat(depositAmount) || null) : null,
        deposit_percentage: depositMode === 'percent' ? (parseFloat(depositPercent) || null) : null,
        reference_quote_number: referenceQuoteNumber || null
      });
      
      setPreviewHtml(response.data);
      setShowPreview(true);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error('Vorschau fehlgeschlagen');
    } finally {
      setIsPreviewing(false);
    }
  };
  
  // Mahnwesen: Rechnung → 1. → 2. → 3. Mahnung
  const openDunningDialog = (doc) => {
    setDunningDoc(doc);
    setShowDunningDialog(true);
  };

  const canCreateDunning = (doc) => {
    if (['rechnung', 'abschlag'].includes(doc.document_type)) {
      return !['bezahlt', 'storniert'].includes(doc.status) && (doc.mahnstufe || 0) < 3;
    }
    if (doc.document_type !== 'mahnung' || (doc.dunning_level || 0) >= 3) return false;
    const inv = archive.find((a) => a.id === doc.reference_invoice_id);
    return !!inv && (inv.mahnstufe || 0) === doc.dunning_level && !['bezahlt', 'storniert'].includes(inv.status);
  };

  const nextDunningLabel = (doc) => {
    const current = doc.document_type === 'mahnung' ? doc.dunning_level : (doc.mahnstufe || 0);
    return `${current + 1}. Mahnung`;
  };
  
  // Download from archive
  const downloadArchiveDoc = async (doc) => {
    try {
      const response = await api.get(`/admin/documents/${doc.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.document_type}_${doc.document_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download fehlgeschlagen');
    }
  };
  
  // Open the send-email dialog for a document (supports scheduling)
  const sendDocumentEmail = (doc) => {
    setEmailDialog({
      open: true,
      doc,
      email: doc.recipient_email || '',
      schedule: false,
      scheduledAt: '',
      sending: false,
    });
  };

  const confirmSendDocumentEmail = async () => {
    const { doc, email, schedule, scheduledAt } = emailDialog;
    if (!doc) return;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }
    let scheduledIso = null;
    if (schedule) {
      if (!scheduledAt) {
        toast.error('Bitte Datum und Uhrzeit für den geplanten Versand wählen');
        return;
      }
      const dt = new Date(scheduledAt);
      if (isNaN(dt.getTime())) {
        toast.error('Ungültiges Datum/Uhrzeit');
        return;
      }
      if (dt.getTime() <= Date.now() + 30 * 1000) {
        toast.error('Der geplante Versand muss in der Zukunft liegen (min. 30 s)');
        return;
      }
      scheduledIso = dt.toISOString();
    }

    setEmailDialog((p) => ({ ...p, sending: true }));
    try {
      const body = { email };
      if (scheduledIso) body.scheduled_at = scheduledIso;
      await api.post(`/admin/documents/${doc.id}/send-email`, body);
      if (scheduledIso) {
        const local = new Date(scheduledIso);
        const localeStr = local.toLocaleString('de-DE', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
        toast.success(`Versand geplant für ${localeStr} Uhr`);
      } else {
        toast.success('E-Mail versendet');
      }
      setEmailDialog({ open: false, doc: null, email: '', schedule: false, scheduledAt: '', sending: false });
    } catch (error) {
      const detail = error?.response?.data?.detail || 'E-Mail konnte nicht gesendet werden';
      toast.error(detail, { duration: 8000 });
      setEmailDialog((p) => ({ ...p, sending: false }));
    }
  };
  
  // Delete document
  const deleteDocument = async (doc) => {
    if (!confirm(`${doc.document_number} wirklich löschen?`)) return;
    
    try {
      await api.delete(`/admin/documents/${doc.id}`);
      toast.success('Dokument gelöscht');
      loadArchive();
    } catch (error) {
      toast.error('Löschen fehlgeschlagen');
    }
  };
  
  // Convert document to another type (Angebot → Rechnung/AB/Abschlag)
  const convertDocument = async (doc, newType) => {
    try {
      const typeLabels = { rechnung: 'Rechnung', angebot: 'Angebot', ab: 'Auftragsbestätigung', auftragsbestaetigung: 'Auftragsbestätigung', abschlag: 'Abschlagsrechnung' };
      
      // Load full document data from API so we have ALL fields (email, phone, contact, delivery, iban, ...)
      let fullDoc = doc;
      try {
        const response = await api.get(`/admin/documents/${doc.id}`);
        fullDoc = { ...doc, ...response.data };
      } catch (e) {
        console.log('Convert: fallback to archive data');
      }
      
      // Set document type
      setDocumentType(newType);
      
      // Default intro text for the new type
      const defaultIntro = getDefaultIntroText(newType, fullDoc);
      
      // If converting to Abschlagsrechnung: capture the quote number as reference
      if (newType === 'abschlag' && doc.document_type === 'angebot') {
        setReferenceQuoteNumber(doc.document_number || '');
        setDepositMode('percent');
        setDepositPercent('30');
        setDepositAmount('');
      } else {
        setReferenceQuoteNumber('');
      }
      
      // Fill customer (dropdown) if customer exists in the CRM
      if (fullDoc.customer_number) {
        const customer = customers.find(c => c.customer_number === fullDoc.customer_number);
        if (customer) {
          setSelectedCustomer(customer);
          setCustomerSearch(`${customer.first_name} ${customer.last_name}`);
        }
      }
      
      // Fill billing address
      setBillingAddress({
        name: fullDoc.billing_name || '',
        street: fullDoc.billing_street || '',
        city: fullDoc.billing_city || '',
        country: fullDoc.billing_country || 'Germany'
      });
      
      // Fill delivery address
      const sameAddr = !fullDoc.delivery_name ||
                       (fullDoc.billing_name === fullDoc.delivery_name && fullDoc.billing_street === fullDoc.delivery_street);
      setDeliveryAddress({
        name: fullDoc.delivery_name || '',
        street: fullDoc.delivery_street || '',
        city: fullDoc.delivery_city || '',
        country: fullDoc.delivery_country || 'Germany',
        sameAsBilling: sameAddr
      });
      
      // Fill contact (name, phone, EMAIL, USt-ID)
      setContact({
        name: fullDoc.recipient_contact || '',
        phone: fullDoc.recipient_phone || '',
        email: fullDoc.recipient_email || '',
        vatId: fullDoc.recipient_vat_id || ''
      });
      
      // Fill positions
      if (fullDoc.positions && fullDoc.positions.length > 0) {
        setPositions(fullDoc.positions.map((p, idx) => ({
          pos: p.pos || idx + 1,
          description: p.description || '',
          price: p.price || '',
          quantity: p.quantity || 1,
          total: p.total || '',
          textFormat: p.text_format || 'normal',
          is_text: !!p.is_text,
          is_subtotal: !!p.is_subtotal
        })));
      } else {
        toast.error(
          'Dieses Dokument wurde vor dem Update erstellt und hat keine gespeicherten Positionen. ' +
          'Bitte die Positionen manuell neu eingeben.',
          { duration: 8000 }
        );
        setPositions([{ pos: 1, description: '', price: '', quantity: 1, total: '', textFormat: 'normal' }]);
      }
      
      // Steuer & Zahlung übernehmen
      if (fullDoc.tax_rate !== undefined && fullDoc.tax_rate !== null) setTaxRate(fullDoc.tax_rate);
      setPayment(prev => ({
        terms: fullDoc.payment_terms || prev.terms || '14 Tage netto nach Rechnungserhalt',
        iban: fullDoc.iban || prev.iban || '',
        bic: fullDoc.bic || prev.bic || '',
        bankName: fullDoc.bank_name || prev.bankName || ''
      }));
      if (fullDoc.footer_text) setFooterText(fullDoc.footer_text);
      
      // Set reference to original document + default intro
      setDocData(prev => ({
        ...prev,
        referenceNumber: fullDoc.document_number,
        date: new Date().toLocaleDateString('de-DE'),
        introText: defaultIntro,
        noteText: prev.noteText || ''
      }));
      
      // Reset draft mode (this is a new document, not an edit)
      setCurrentDraftId(null);
      
      // Switch to create tab
      setActiveTab('create');
      toast.success(`Kundendaten übernommen — jetzt als ${typeLabels[newType]} bearbeiten`);
    } catch (error) {
      console.error('Convert failed:', error);
      toast.error('Weiterführung fehlgeschlagen');
    }
  };
  
  // Default intro text depending on document type
  const getDefaultIntroText = (docType, sourceDoc = null) => {
    const refNum = sourceDoc?.document_number || '';
    if (docType === 'abschlag') {
      return `vielen Dank für Ihren Auftrag und Ihr entgegengebrachtes Vertrauen.

Wie mit Ihnen besprochen und im Angebot${refNum ? ` Nr. ${refNum}` : ''} vereinbart, stellen wir Ihnen hiermit eine Abschlagsrechnung für die anteilige Bezahlung der beauftragten Leistungen. Der Abschlagsbetrag deckt die zur Ausführung notwendigen Vorkosten (Material, Anfahrt, Personalplanung) und wird vollständig mit der Schlussrechnung nach Abschluss aller Arbeiten verrechnet.

Bitte überweisen Sie den unten aufgeführten Abschlagsbetrag innerhalb der genannten Zahlungsfrist auf das angegebene Konto. Nach Zahlungseingang beginnen wir zum vereinbarten Termin mit der Ausführung. Bei Rückfragen sind wir jederzeit für Sie da.`;
    }
    if (docType === 'rechnung') {
      return `vielen Dank für Ihren Auftrag${refNum ? ` (Bezug: ${refNum})` : ''}. Wie vereinbart erlauben wir uns, Ihnen die erbrachten Leistungen wie folgt in Rechnung zu stellen.`;
    }
    if (docType === 'angebot') {
      return `vielen Dank für Ihre Anfrage. Gerne unterbreiten wir Ihnen nachfolgend unser unverbindliches Angebot für die von Ihnen gewünschten Leistungen.`;
    }
    if (docType === 'auftragsbestaetigung') {
      return `vielen Dank für Ihren Auftrag${refNum ? ` auf Basis unseres Angebots ${refNum}` : ''}. Hiermit bestätigen wir die Beauftragung der nachstehenden Leistungen und werden diese wie vereinbart ausführen.`;
    }
    return '';
  };

  // Convert document to Gutachten
  const convertToGutachten = async (doc) => {
    setConvertingToGutachten(doc.id);
    try {
      const response = await api.post(`/admin/documents/${doc.id}/to-gutachten`);
      const data = response.data;
      
      // Store the gutachten data in localStorage for the Gutachten page to pick up
      localStorage.setItem('gutachten_from_document', JSON.stringify(data.gutachten_data));
      
      toast.success(data.message || 'Gutachten wurde erstellt');
      
      // Navigate to Gutachten page
      navigate('/admin/gutachten');
    } catch (error) {
      console.error('Gutachten conversion failed:', error);
      toast.error(error.response?.data?.detail || 'Gutachten-Umwandlung fehlgeschlagen');
    } finally {
      setConvertingToGutachten(null);
    }
  };
  
  // Edit existing document
  const editDocument = async (doc) => {
    try {
      // Load full document data from API
      let fullDoc = doc;
      try {
        const response = await api.get(`/admin/documents/${doc.id}`);
        fullDoc = response.data;
      } catch (e) {
        console.log('Using archive data directly');
      }
      
      // Set document type
      setDocumentType(fullDoc.document_type);
      
      // Fill customer data
      if (fullDoc.customer_number) {
        const customer = customers.find(c => c.customer_number === fullDoc.customer_number);
        if (customer) {
          setSelectedCustomer(customer);
          setCustomerSearch(`${customer.first_name} ${customer.last_name}`);
        }
      }
      
      // Fill billing address
      setBillingAddress({
        name: fullDoc.billing_name || '',
        street: fullDoc.billing_street || '',
        city: fullDoc.billing_city || '',
        country: 'Germany'
      });
      
      // Fill positions - check if positions exist
      const hasPositions = fullDoc.positions && fullDoc.positions.length > 0;
      if (hasPositions) {
        setPositions(fullDoc.positions.map((p, idx) => ({
          pos: p.pos || idx + 1,
          description: p.description || '',
          price: p.price || '',
          quantity: p.quantity || 1,
          total: p.total || '',
          textFormat: p.text_format || 'normal',
          is_text: !!p.is_text,
          is_subtotal: !!p.is_subtotal
        })));
      } else {
        // If no positions saved, create empty position
        setPositions([{ pos: 1, description: '', price: '', quantity: 1, total: '', textFormat: 'normal' }]);
      }
      
      // Fill document data
      setDocData(prev => ({
        ...prev,
        documentNumber: fullDoc.document_number || '',
        date: fullDoc.document_date || new Date().toLocaleDateString('de-DE'),
        introText: fullDoc.intro_text || prev.introText || '',
        noteText: fullDoc.note_text || prev.noteText || ''
      }));
      
      // Store original doc ID for update
      setCurrentDraftId(fullDoc.id);
      
      // Switch to create tab
      setActiveTab('create');
      
      // Show appropriate toast message AFTER all state updates
      if (!hasPositions) {
        toast.error(
          'Dieses Dokument wurde vor dem Update erstellt und hat keine gespeicherten Positionen. ' +
          'Bitte die Positionen manuell neu eingeben oder das PDF herunterladen und abschreiben.',
          { duration: 10000 }
        );
      } else {
        toast.info(`Dokument ${fullDoc.document_number} wird bearbeitet`);
      }
    } catch (error) {
      console.error('Edit failed:', error);
      toast.error('Bearbeitung fehlgeschlagen');
    }
  };
  
  // Regenerate PDF for an archived document (fixes missing sections after backend fixes)
  const regeneratePdf = async (doc) => {
    setRegeneratingDocId(doc.id);
    try {
      await api.post(`/admin/documents/${doc.id}/regenerate-pdf`);
      toast.success(`PDF für ${doc.document_number} neu erzeugt`);
      // Fetch the fresh PDF immediately (cache-busted) so user sees new content
      try {
        const bust = Date.now();
        const response = await api.get(`/admin/documents/${doc.id}/pdf?v=${bust}`, {
          responseType: 'blob',
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${doc.document_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } catch (e) {
        console.error('Fresh PDF download failed:', e);
      }
      loadArchive();
    } catch (error) {
      console.error('PDF regenerate failed:', error);
      toast.error(error.response?.data?.detail || 'Neuerzeugung fehlgeschlagen');
    } finally {
      setRegeneratingDocId(null);
    }
  };
  
  // Open recipient/address change dialog for archived doc
  const openRecipientDialog = async (doc) => {
    let full = doc;
    try {
      const r = await api.get(`/admin/documents/${doc.id}`);
      full = { ...doc, ...r.data };
    } catch (e) {
      console.log('recipient dialog: using archive data');
    }
    const isDeliverySame = !full.delivery_name ||
      (full.billing_name === full.delivery_name && full.billing_street === full.delivery_street);
    setRecipientEditDoc(full);
    setRecipientForm({
      billing_name: full.billing_name || '',
      billing_street: full.billing_street || '',
      billing_city: full.billing_city || '',
      delivery_name: full.delivery_name || '',
      delivery_street: full.delivery_street || '',
      delivery_city: full.delivery_city || '',
      recipient_email: full.recipient_email || '',
      recipient_phone: full.recipient_phone || '',
      recipient_contact: full.recipient_contact || '',
      recipient_vat_id: full.recipient_vat_id || '',
      delivery_same_as_billing: isDeliverySame,
    });
    setShowRecipientDialog(true);
  };
  
  const saveRecipientChanges = async () => {
    if (!recipientEditDoc) return;
    setSavingRecipient(true);
    try {
      const payload = {
        billing_name: recipientForm.billing_name,
        billing_street: recipientForm.billing_street,
        billing_city: recipientForm.billing_city,
        recipient_email: recipientForm.recipient_email,
        recipient_phone: recipientForm.recipient_phone,
        recipient_contact: recipientForm.recipient_contact,
        recipient_vat_id: recipientForm.recipient_vat_id,
      };
      if (recipientForm.delivery_same_as_billing) {
        payload.delivery_name = recipientForm.billing_name;
        payload.delivery_street = recipientForm.billing_street;
        payload.delivery_city = recipientForm.billing_city;
      } else {
        payload.delivery_name = recipientForm.delivery_name;
        payload.delivery_street = recipientForm.delivery_street;
        payload.delivery_city = recipientForm.delivery_city;
      }
      await api.put(`/admin/documents/${recipientEditDoc.id}/recipient`, payload);
      toast.success('Empfänger aktualisiert und PDF neu erzeugt');
      setShowRecipientDialog(false);
      setRecipientEditDoc(null);
      loadArchive();
    } catch (error) {
      console.error('Recipient update failed:', error);
      toast.error(error.response?.data?.detail || 'Aktualisierung fehlgeschlagen');
    } finally {
      setSavingRecipient(false);
    }
  };
  
  // Save settings
  const saveSettings = async () => {
    try {
      // Unveränderte Nummernzähler nicht zurückschreiben (sonst doppelte Belegnummern)
      const loaded = loadedSettingsRef.current || {};
      const payload = Object.fromEntries(Object.entries(settings).filter(
        ([k, v]) => !(k.endsWith('_next_number') && v === loaded[k])
      ));
      await api.put('/admin/documents/settings', payload);
      loadedSettingsRef.current = { ...loaded, ...payload };
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    }
  };
  
  // Upload logo
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/admin/documents/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo hochgeladen');
    } catch (error) {
      toast.error('Upload fehlgeschlagen');
    }
  };

  return (
    <div className="p-4 md:p-6" data-testid="document-generator">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 md:w-8 md:h-8 text-[#9B1B30]" />
          <h1 className="text-xl md:text-2xl font-bold">FarbWelt Office</h1>
        </div>
        <Button 
          variant="outline" 
          onClick={loadDrafts}
          disabled={isLoadingDrafts}
          className="border-amber-500 text-amber-600 hover:bg-amber-50"
        >
          <FolderOpen className="w-4 h-4 mr-2" />
          {isLoadingDrafts ? 'Lädt...' : 'Entwürfe laden'}
        </Button>
      </div>

      {/* Document Profile Switcher */}
      <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3" data-testid="profile-switcher">
        <span className="text-xs font-semibold text-gray-500 mr-1">Konto:</span>
        {profiles.map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActiveProfile(p.id)}
            data-testid={`profile-tab-${p.id}`}
            className={
              'px-3 py-1.5 rounded-full text-xs font-medium transition ' +
              (activeProfile === p.id
                ? 'bg-[#9B1B30] text-white shadow'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
            }
          >
            {p.name}
            {p.prefix_suffix ? <span className="ml-1 opacity-70">[{p.prefix_suffix}]</span> : null}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setNewProfileDialog({
            open: true, name: '', prefix_suffix: '',
            invoice_next_number: 1, quote_next_number: 1, order_next_number: 1, saving: false,
          })}
          data-testid="profile-add-btn"
          className="ml-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-dashed border-gray-400 text-gray-600 hover:border-[#9B1B30] hover:text-[#9B1B30]"
        >
          + Konto hinzufügen
        </button>
        {activeProfile !== 'default' && (
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm('Dieses Konto wirklich löschen? (Nur möglich, wenn keine Dokumente drin sind.)')) return;
              try {
                await api.delete(`/admin/document-profiles/${activeProfile}`);
                toast.success('Konto gelöscht');
                setActiveProfile('default');
                loadProfiles();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
              }
            }}
            data-testid="profile-delete-btn"
            className="ml-auto px-2 py-1.5 rounded text-xs text-red-500 hover:bg-red-50"
            title="Aktuelles Konto löschen"
          >
            Konto löschen
          </button>
        )}
      </div>

      {/* Entwürfe Dialog */}
      <Dialog open={showDraftsDialog} onOpenChange={setShowDraftsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-amber-500" />
              Gespeicherte Entwürfe
            </DialogTitle>
          </DialogHeader>
          {drafts.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Keine Entwürfe vorhanden</p>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {drafts.map((draft) => (
                <div 
                  key={draft.id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        draft.document_type === 'rechnung' ? 'bg-blue-100 text-blue-700' :
                        draft.document_type === 'angebot' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {draft.document_type === 'rechnung' ? 'Rechnung' : 
                         draft.document_type === 'angebot' ? 'Angebot' : 'AB'}
                      </span>
                      <span className="font-medium">{draft.billing_name || 'Ohne Name'}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {draft.document_number && <span className="mr-3">Nr: {draft.document_number}</span>}
                      <span>Gespeichert: {new Date(draft.updated_at || draft.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button 
                      size="sm" 
                      onClick={() => loadDraftIntoForm(draft)}
                      className="bg-[#9B1B30] hover:bg-[#7a1526]"
                    >
                      Laden
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => deleteDraft(draft.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Mahnung erstellen Dialog */}
      <DunningDialog
        doc={dunningDoc}
        open={showDunningDialog}
        onOpenChange={setShowDunningDialog}
        onCreated={() => loadArchive()}
        onSendEmail={sendDocumentEmail}
      />

      {/* Empfänger / Lieferadresse ändern Dialog (für archivierte Dokumente) */}
      <Dialog open={showRecipientDialog} onOpenChange={(open) => { setShowRecipientDialog(open); if (!open) setRecipientEditDoc(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#9B1B30]" />
              Empfänger & Adressen ändern
            </DialogTitle>
            {recipientEditDoc && (
              <p className="text-sm text-gray-500 pt-1">
                Dokument: <strong>{recipientEditDoc.document_number}</strong>
                {' · '}Das PDF wird sofort neu erzeugt. Dokumentennummer und Positionen bleiben unverändert.
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Rechnungsempfänger */}
            <div>
              <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#9B1B30] rounded-full"></span>
                Rechnungsempfänger (Rechnungsanschrift)
              </div>
              <div className="grid grid-cols-1 gap-2">
                <Input
                  placeholder="Name / Firma *"
                  value={recipientForm.billing_name}
                  onChange={(e) => setRecipientForm({...recipientForm, billing_name: e.target.value})}
                  data-testid="recipient-billing-name"
                />
                <Input
                  placeholder="Straße + Nr. *"
                  value={recipientForm.billing_street}
                  onChange={(e) => setRecipientForm({...recipientForm, billing_street: e.target.value})}
                  data-testid="recipient-billing-street"
                />
                <Input
                  placeholder="PLZ Ort *"
                  value={recipientForm.billing_city}
                  onChange={(e) => setRecipientForm({...recipientForm, billing_city: e.target.value})}
                  data-testid="recipient-billing-city"
                />
              </div>
            </div>
            
            {/* Lieferadresse */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <span className="w-1 h-4 bg-[#9B1B30] rounded-full"></span>
                  Lieferadresse
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recipientForm.delivery_same_as_billing}
                    onChange={(e) => setRecipientForm({...recipientForm, delivery_same_as_billing: e.target.checked})}
                    className="accent-[#9B1B30]"
                    data-testid="recipient-delivery-same"
                  />
                  identisch mit Rechnungsempfänger
                </label>
              </div>
              {!recipientForm.delivery_same_as_billing && (
                <div className="grid grid-cols-1 gap-2">
                  <Input
                    placeholder="Name / Firma (Lieferung)"
                    value={recipientForm.delivery_name}
                    onChange={(e) => setRecipientForm({...recipientForm, delivery_name: e.target.value})}
                    data-testid="recipient-delivery-name"
                  />
                  <Input
                    placeholder="Straße + Nr."
                    value={recipientForm.delivery_street}
                    onChange={(e) => setRecipientForm({...recipientForm, delivery_street: e.target.value})}
                    data-testid="recipient-delivery-street"
                  />
                  <Input
                    placeholder="PLZ Ort"
                    value={recipientForm.delivery_city}
                    onChange={(e) => setRecipientForm({...recipientForm, delivery_city: e.target.value})}
                    data-testid="recipient-delivery-city"
                  />
                </div>
              )}
            </div>
            
            {/* Kontakt */}
            <div>
              <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#9B1B30] rounded-full"></span>
                Kontaktdaten
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  placeholder="Ansprechpartner"
                  value={recipientForm.recipient_contact}
                  onChange={(e) => setRecipientForm({...recipientForm, recipient_contact: e.target.value})}
                  data-testid="recipient-contact-name"
                />
                <Input
                  type="email"
                  placeholder="E-Mail"
                  value={recipientForm.recipient_email}
                  onChange={(e) => setRecipientForm({...recipientForm, recipient_email: e.target.value})}
                  data-testid="recipient-email"
                />
                <Input
                  type="tel"
                  placeholder="Telefon"
                  value={recipientForm.recipient_phone}
                  onChange={(e) => setRecipientForm({...recipientForm, recipient_phone: e.target.value})}
                  data-testid="recipient-phone"
                />
                <Input
                  placeholder="USt-IdNr. (optional)"
                  value={recipientForm.recipient_vat_id}
                  onChange={(e) => setRecipientForm({...recipientForm, recipient_vat_id: e.target.value})}
                  data-testid="recipient-vat-id"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowRecipientDialog(false)} className="flex-1" disabled={savingRecipient}>
              Abbrechen
            </Button>
            <Button onClick={saveRecipientChanges} className="flex-1 bg-[#9B1B30] hover:bg-[#7A1525] text-white" disabled={savingRecipient} data-testid="recipient-save-btn">
              {savingRecipient ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern & PDF neu erzeugen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aufmaß Import Dialog */}
      <Dialog open={showAufmassDialog} onOpenChange={setShowAufmassDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ruler className="w-5 h-5 text-[#4a6741]" />
              Aufmaß übernehmen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {aufmassList.length === 0 ? (
              <p className="text-center text-gray-500 py-6">Keine Aufmaße vorhanden. Erstellen Sie zuerst ein Aufmaß unter "Aufmaß" in der Sidebar.</p>
            ) : (
              aufmassList.map((aufmass) => {
                const wallArea = (aufmass.walls || []).reduce((s, w) => s + ((parseFloat(w.breite) || 0) * (parseFloat(w.hoehe) || 0)), 0);
                return (
                  <div
                    key={aufmass.id}
                    onClick={() => importFromAufmass(aufmass)}
                    className="p-3 border rounded-lg cursor-pointer hover:border-[#4a6741] hover:bg-[#4a6741]/5 transition-colors"
                    data-testid={`aufmass-import-${aufmass.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{aufmass.customer_name || 'Ohne Kunde'}</p>
                        <p className="text-sm text-gray-500">
                          {aufmass.objekt && <span className="mr-3">{aufmass.objekt}</span>}
                          {aufmass.besichtigung_date && <span>Besichtigung: {new Date(aufmass.besichtigung_date).toLocaleDateString('de-DE')}</span>}
                        </p>
                      </div>
                      {wallArea > 0 && <span className="text-sm font-medium text-[#4a6741]">{wallArea.toFixed(1)} m²</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(aufmass.arbeiten || []).slice(0, 4).map(a => (
                        <span key={a} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{a}</span>
                      ))}
                      {(aufmass.arbeiten || []).length > 4 && (
                        <span className="text-xs text-gray-400">+{aufmass.arbeiten.length - 4}</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 md:mb-6 w-full sm:w-auto flex-wrap">
          <TabsTrigger value="create" className="flex-1 sm:flex-none text-xs sm:text-sm">Neues Dokument</TabsTrigger>
          <TabsTrigger value="archive" className="flex-1 sm:flex-none text-xs sm:text-sm">Archiv ({archive.length})</TabsTrigger>
          <TabsTrigger value="settings" className="flex-1 sm:flex-none text-xs sm:text-sm">Einstellungen</TabsTrigger>
        </TabsList>
        
        {/* CREATE TAB */}
        <TabsContent value="create" className="space-y-4 md:space-y-6">
          {/* Document Type & Customer Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Dokumenttyp</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-4">
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger data-testid="document-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rechnung">Rechnung</SelectItem>
                    <SelectItem value="angebot">Angebot</SelectItem>
                    <SelectItem value="auftragsbestaetigung">Auftragsbestätigung</SelectItem>
                    <SelectItem value="abschlag" data-testid="doctype-abschlag">Abschlagsrechnung</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" className="w-full border-[#4a6741]/30 text-[#4a6741] hover:bg-[#4a6741]/5" onClick={loadAufmassForImport} data-testid="import-aufmass-btn">
                  <Ruler className="w-4 h-4 mr-2" />
                  Von Aufmaß übernehmen
                </Button>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">{documentType === 'rechnung' ? 'Rechnungsnummer' : documentType === 'angebot' ? 'Angebotsnummer' : documentType === 'abschlag' ? 'Abschlagsnr.' : 'AB-Nummer'}</Label>
                    <Input 
                      value={docData.documentNumber}
                      onChange={(e) => setDocData({...docData, documentNumber: e.target.value})}
                      placeholder="Automatisch"
                      data-testid="document-number-input"
                    />
                  </div>
                  <div>
                    <Label>Kundennummer</Label>
                    <Input 
                      value={docData.customerNumber}
                      onChange={(e) => setDocData({...docData, customerNumber: e.target.value})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Kunde auswählen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Kunde suchen (Name, Nr., E-Mail, Ort)..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="pl-10"
                    data-testid="doc-customer-search-input"
                  />
                </div>
                
                <Select onValueChange={(val) => {
                  const customer = customers.find(c => c.id === val);
                  if (customer) {
                    selectCustomer(customer);
                    setCustomerSearch('');
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kunde aus Liste wählen..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {filteredCustomers.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        {customerSearch ? 'Keine Kunden gefunden' : 'Keine Kunden vorhanden'}
                      </div>
                    ) : (
                      filteredCustomers.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{c.customer_number} - {c.first_name} {c.last_name}</span>
                            <span className="text-xs text-gray-500">{c.city} {c.email && `• ${c.email}`}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {selectedCustomer && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ {selectedCustomer.first_name} {selectedCustomer.last_name} ausgewählt
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Addresses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Card>
              <CardHeader className="p-4 md:p-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base md:text-lg">Rechnungsanschrift</CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const address = `${billingAddress.name}\n${billingAddress.street}\n${billingAddress.city}${billingAddress.country ? '\n' + billingAddress.country : ''}`;
                      navigator.clipboard.writeText(address);
                      toast.success('Adresse kopiert');
                    }}
                    title="Adresse in Zwischenablage kopieren"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-3 md:space-y-4">
                <div>
                  <Label className="text-sm">Name / Firma</Label>
                  <Input 
                    value={billingAddress.name}
                    onChange={(e) => setBillingAddress({...billingAddress, name: e.target.value})}
                    data-testid="billing-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm">Straße</Label>
                  <AddressAutocomplete
                    value={billingAddress.street}
                    onChange={(val) => setBillingAddress({...billingAddress, street: val})}
                    onAddressSelect={(addr) => {
                      setBillingAddress({
                        ...billingAddress,
                        street: addr.street,
                        city: addr.postcode && addr.city ? `${addr.postcode} ${addr.city}` : billingAddress.city
                      });
                    }}
                    placeholder="Straße eingeben..."
                    className="[&_input]:bg-white [&_input]:border-gray-300 [&_input]:text-gray-900 [&_input]:placeholder-gray-400"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label className="text-sm">PLZ / Ort</Label>
                    <Input 
                      value={billingAddress.city}
                      onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Land</Label>
                    <Input 
                      value={billingAddress.country}
                      onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <CardTitle className="text-base md:text-lg">Lieferanschrift</CardTitle>
                  <label className="flex items-center gap-2 text-xs sm:text-sm">
                    <input 
                      type="checkbox"
                      checked={deliveryAddress.sameAsBilling}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, sameAsBilling: e.target.checked})}
                    />
                    Wie Rechnungsanschrift
                  </label>
                </div>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-3 md:space-y-4">
                <div>
                  <Label className="text-sm">Name / Firma</Label>
                  <Input 
                    value={deliveryAddress.sameAsBilling ? billingAddress.name : deliveryAddress.name}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, name: e.target.value})}
                    disabled={deliveryAddress.sameAsBilling}
                  />
                </div>
                <div>
                  <Label className="text-sm">Straße</Label>
                  <AddressAutocomplete
                    value={deliveryAddress.sameAsBilling ? billingAddress.street : deliveryAddress.street}
                    onChange={(val) => setDeliveryAddress({...deliveryAddress, street: val})}
                    onAddressSelect={(addr) => {
                      setDeliveryAddress({
                        ...deliveryAddress,
                        street: addr.street,
                        city: addr.postcode && addr.city ? `${addr.postcode} ${addr.city}` : deliveryAddress.city
                      });
                    }}
                    placeholder="Straße eingeben..."
                    disabled={deliveryAddress.sameAsBilling}
                    className="[&_input]:bg-white [&_input]:border-gray-300 [&_input]:text-gray-900 [&_input]:placeholder-gray-400"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label className="text-sm">PLZ / Ort</Label>
                    <Input 
                      value={deliveryAddress.sameAsBilling ? billingAddress.city : deliveryAddress.city}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, city: e.target.value})}
                      disabled={deliveryAddress.sameAsBilling}
                    />
                  </div>
                  <div>
                    <Label>Land</Label>
                    <Input 
                      value={deliveryAddress.sameAsBilling ? billingAddress.country : deliveryAddress.country}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, country: e.target.value})}
                      disabled={deliveryAddress.sameAsBilling}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Document Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dokumentdaten</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Datum</Label>
                  <Input 
                    value={docData.date}
                    onChange={(e) => setDocData({...docData, date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Lieferdatum</Label>
                  <Input 
                    value={docData.deliveryDate}
                    onChange={(e) => setDocData({...docData, deliveryDate: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Referenznummer</Label>
                  <Input 
                    value={docData.referenceNumber}
                    onChange={(e) => setDocData({...docData, referenceNumber: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm">Belegnummer</Label>
                  <Input 
                    value={docData.contractNumber}
                    onChange={(e) => setDocData({...docData, contractNumber: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <Label>Ansprechpartner</Label>
                  <Input 
                    value={contact.name}
                    onChange={(e) => setContact({...contact, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input 
                    value={contact.phone}
                    onChange={(e) => setContact({...contact, phone: e.target.value})}
                  />
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input 
                    value={contact.email}
                    onChange={(e) => setContact({...contact, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm">USt-IdNr. Kunde</Label>
                  <Input 
                    value={contact.vatId}
                    onChange={(e) => setContact({...contact, vatId: e.target.value})}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Intro Text before Positions */}
          <Card>
            <CardHeader className="p-4 md:p-6 pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base md:text-lg">Einleitungstext (optional)</CardTitle>
                <Select onValueChange={(templateId) => {
                  const template = textTemplates.find(t => t.id === templateId);
                  if (template) {
                    setDocData({...docData, introText: template.text});
                  }
                }}>
                  <SelectTrigger className="w-[180px] h-8 text-sm">
                    <SelectValue placeholder="Vorlage wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {textTemplates.filter(t => t.type === 'intro').map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              <Textarea 
                value={docData.introText || ''}
                onChange={(e) => setDocData({...docData, introText: e.target.value})}
                placeholder="z.B. Vielen Dank für Ihre Anfrage. Gerne unterbreiten wir Ihnen folgendes Angebot..."
                className="min-h-[60px]"
              />
              <p className="text-xs text-gray-500 mt-1">Erscheint oberhalb der Positionen auf dem Dokument</p>
            </CardContent>
          </Card>
          
          {/* Positions */}
          <Card>
            <CardHeader className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-base md:text-lg">Positionen</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowPositionTemplates(true)}>
                    <FileText className="w-4 h-4 mr-1" /> Vorlage
                  </Button>
                  <Button variant="outline" size="sm" onClick={addPosition} data-testid="add-position-btn">
                    <Plus className="w-4 h-4 mr-1" /> Position
                  </Button>
                  <Button variant="outline" size="sm" onClick={addTextBlock} data-testid="add-text-block-btn">
                    <Type className="w-4 h-4 mr-1" /> Freitext
                  </Button>
                  <Button variant="outline" size="sm" onClick={addSubtotal} data-testid="add-subtotal-btn">
                    <Sigma className="w-4 h-4 mr-1" /> Zwischensumme
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    setPositions(renumber([...positions, { description: '', price: 0, quantity: 1, is_optional: true }]));
                  }} className="text-gray-500 border-dashed">
                    <Plus className="w-4 h-4 mr-1" /> Optional
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              {/* Position Templates Dialog */}
              <Dialog open={showPositionTemplates} onOpenChange={setShowPositionTemplates}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Position aus Vorlage einfügen</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {/* Group by category */}
                    {Object.entries(
                      positionTemplates.reduce((acc, t) => {
                        if (!acc[t.category]) acc[t.category] = [];
                        acc[t.category].push(t);
                        return acc;
                      }, {})
                    ).map(([category, templates]) => (
                      <div key={category}>
                        <h4 className="font-medium text-sm text-gray-700 mb-2">{category}</h4>
                        <div className="grid gap-2">
                          {templates.map(t => (
                            <button
                              key={t.id}
                              onClick={() => {
                                setPositions(renumber([...positions, {
                                  description: t.description,
                                  price: t.default_price,
                                  quantity: 1,
                                  is_optional: false
                                }]));
                                setShowPositionTemplates(false);
                                toast.success('Position hinzugefügt');
                              }}
                              className="flex justify-between items-center p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg border"
                            >
                              <span className="text-sm">{t.description}</span>
                              <span className="text-sm font-medium text-[#9B1B30]">{t.default_price?.toFixed(2)} € / {t.unit}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Mobile: Card-basierte Positionen */}
              <div className="block md:hidden space-y-4">
                {positions.map((pos, index) => pos.is_subtotal ? (
                  <SubtotalCard key={index} index={index} count={positions.length} {...subtotalAt(index)} reverseCharge={reverseCharge} onMove={movePosition} onRemove={removePosition} />
                ) : pos.is_text ? (
                  <div key={index} className="border border-[#9B1B30]/30 rounded-lg p-3 space-y-2 bg-white" data-testid={`text-block-mobile-${index}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm flex items-center gap-1 text-[#9B1B30]"><Type className="w-4 h-4" /> Freitext</span>
                      <div className="flex items-center">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => movePosition(index, -1)} disabled={index === 0} data-testid={`mobile-move-up-${index}`}><ChevronUp className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => movePosition(index, 1)} disabled={index === positions.length - 1} data-testid={`mobile-move-down-${index}`}><ChevronDown className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => removePosition(index)} className="h-8 w-8 p-0 text-red-500" data-testid={`mobile-remove-text-block-${index}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    <FormattedTextarea
                      value={pos.description}
                      onChange={(e) => updatePosition(index, 'description', e.target.value)}
                      className="min-h-[70px] text-sm"
                      placeholder="Freitext eingeben (erscheint ohne Nummer und Preis)..."
                      onImprove={() => improveDescription(index)}
                      isImproving={improvingIndex === index}
                    />
                  </div>
                ) : (
                  <div key={index} className={`border rounded-lg p-3 space-y-3 ${pos.is_optional ? 'bg-gray-100 border-dashed' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">
                        Position {pos.pos}
                        {pos.is_optional && <span className="ml-2 text-xs text-gray-500 italic">(Optional)</span>}
                      </span>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs text-gray-500">
                          <input
                            type="checkbox"
                            checked={pos.is_optional || false}
                            onChange={(e) => updatePosition(index, 'is_optional', e.target.checked)}
                            className="w-4 h-4 rounded"
                          />
                          Opt.
                        </label>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removePosition(index)}
                          className="h-8 w-8 p-0 text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <FormattedTextarea
                      value={pos.description}
                      onChange={(e) => updatePosition(index, 'description', e.target.value)}
                      className="min-h-[60px] text-sm"
                      placeholder="Beschreibung eingeben..."
                      onImprove={() => improveDescription(index)}
                      isImproving={improvingIndex === index}
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Preis €</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={pos.price}
                          onChange={(e) => updatePosition(index, 'price', e.target.value)}
                          className="text-right text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Menge</Label>
                        <Input 
                          type="number"
                          value={pos.quantity}
                          onChange={(e) => updatePosition(index, 'quantity', e.target.value)}
                          className="text-center text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Gesamt €</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={pos.total}
                          onChange={(e) => updatePosition(index, 'total', e.target.value)}
                          className="text-right text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop: Tabellen-Layout */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left w-14 text-sm">Pos</th>
                      <th className="p-2 text-left text-sm">Beschreibung</th>
                      <th className="p-2 text-right w-28 text-sm">Preis</th>
                      <th className="p-2 text-center w-24 text-sm">Menge</th>
                      <th className="p-2 text-right w-28 text-sm">Gesamt</th>
                      <th className="p-2 text-center w-16 text-sm">Opt.</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((pos, index) => pos.is_subtotal ? (
                      <SubtotalTableRow key={index} index={index} count={positions.length} {...subtotalAt(index)} reverseCharge={reverseCharge} onMove={movePosition} onRemove={removePosition} />
                    ) : pos.is_text ? (
                      <tr key={index} className="border-b bg-[#9B1B30]/[0.03]" data-testid={`text-block-row-${index}`}>
                        <td className="p-2 align-top">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-[#9B1B30] mt-2"><Type className="w-3.5 h-3.5" />Text</span>
                        </td>
                        <td className="p-2" colSpan={5}>
                          <FormattedTextarea
                            value={pos.description}
                            onChange={(e) => updatePosition(index, 'description', e.target.value)}
                            className="min-h-[60px]"
                            placeholder="Freitext eingeben (erscheint ohne Nummer und Preis)..."
                            onImprove={() => improveDescription(index)}
                            isImproving={improvingIndex === index}
                            data-testid={`text-block-description-${index}`}
                          />
                        </td>
                        <td className="p-2 align-top">
                          <div className="flex flex-col items-center">
                            <button type="button" onClick={() => movePosition(index, -1)} disabled={index === 0} className="p-0.5 text-gray-500 hover:text-gray-900 disabled:opacity-30" data-testid={`move-up-${index}`}><ChevronUp className="w-4 h-4" /></button>
                            <Button variant="ghost" size="sm" onClick={() => removePosition(index)} className="text-red-500 h-7 w-7 p-0" data-testid={`remove-text-block-${index}`}><Trash2 className="w-4 h-4" /></Button>
                            <button type="button" onClick={() => movePosition(index, 1)} disabled={index === positions.length - 1} className="p-0.5 text-gray-500 hover:text-gray-900 disabled:opacity-30" data-testid={`move-down-${index}`}><ChevronDown className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={index} className={`border-b ${pos.is_optional ? 'bg-gray-50 opacity-75' : ''}`}>
                        <td className="p-2">
                          <Input 
                            value={pos.pos}
                            onChange={(e) => updatePosition(index, 'pos', e.target.value)}
                            className="w-12 text-center"
                          />
                        </td>
                        <td className="p-2">
                          <FormattedTextarea
                            value={pos.description}
                            onChange={(e) => updatePosition(index, 'description', e.target.value)}
                            className="min-h-[50px]"
                            placeholder="Beschreibung eingeben..."
                            onImprove={() => improveDescription(index)}
                            isImproving={improvingIndex === index}
                            data-testid={`position-description-${index}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            type="number"
                            step="0.01"
                            value={pos.price}
                            onChange={(e) => updatePosition(index, 'price', e.target.value)}
                            className="text-right"
                            data-testid={`position-price-${index}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            type="number"
                            value={pos.quantity}
                            onChange={(e) => updatePosition(index, 'quantity', e.target.value)}
                            className="text-center w-20"
                          />
                        </td>
                        <td className="p-2">
                          <Input 
                            type="number"
                            step="0.01"
                            value={pos.total}
                            onChange={(e) => updatePosition(index, 'total', e.target.value)}
                            className="text-right"
                            placeholder="Auto"
                          />
                        </td>
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={pos.is_optional || false}
                            onChange={(e) => updatePosition(index, 'is_optional', e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-gray-600"
                            title="Optionale Position (nicht in Summe)"
                          />
                        </td>
                        <td className="p-2">
                          <div className="flex flex-col items-center">
                            <button type="button" onClick={() => movePosition(index, -1)} disabled={index === 0} className="p-0.5 text-gray-500 hover:text-gray-900 disabled:opacity-30" data-testid={`move-up-${index}`}><ChevronUp className="w-4 h-4" /></button>
                            {positions.length > 1 && (
                              <Button variant="ghost" size="sm" onClick={() => removePosition(index)} className="text-red-500 h-7 w-7 p-0">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                            <button type="button" onClick={() => movePosition(index, 1)} disabled={index === positions.length - 1} className="p-0.5 text-gray-500 hover:text-gray-900 disabled:opacity-30" data-testid={`move-down-${index}`}><ChevronDown className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Rabattcode */}
              <div className="mt-4 pt-4 border-t">
                <Label className="text-sm font-medium mb-2 block">Rabattcode (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    value={discountCode}
                    onChange={e => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Code eingeben..."
                    className="font-mono flex-1"
                    disabled={!!discountInfo}
                    data-testid="input-discount-code"
                  />
                  {discountInfo ? (
                    <Button variant="outline" onClick={removeDiscount} className="text-red-500" data-testid="remove-discount-btn">Entfernen</Button>
                  ) : (
                    <Button variant="outline" onClick={validateDiscount} disabled={!discountCode.trim() || isValidatingDiscount} data-testid="validate-discount-btn">
                      {isValidatingDiscount ? '...' : 'Prüfen'}
                    </Button>
                  )}
                </div>
                {discountInfo && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700" data-testid="discount-applied">
                    Rabatt: {discountInfo.type === 'percentage' ? `${discountInfo.value}%` : `${discountInfo.value.toFixed(2)} EUR`} — Ersparnis: {formatCurrency(discountAmount)} EUR
                  </div>
                )}
              </div>
              
              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-full sm:w-72 space-y-1">
                  {discountInfo && (
                    <>
                      <div className="flex justify-between p-2 bg-gray-50 text-sm">
                        <span>Zwischensumme:</span>
                        <span>{formatCurrency(netTotal)} EUR</span>
                      </div>
                      <div className="flex justify-between p-2 bg-red-50 text-sm text-[#9B1B30] font-medium" data-testid="discount-line">
                        <span>Rabatt ({discountInfo.type === 'percentage' ? `${discountInfo.value}%` : `${discountInfo.value.toFixed(2)} EUR`}):</span>
                        <span>-{formatCurrency(discountAmount)} EUR</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between p-2 bg-gray-50 text-sm">
                    <span>Netto:</span>
                    <span>{formatCurrency(netAfterDiscount)} EUR</span>
                  </div>
                  {reverseCharge ? (
                    <div className="flex justify-between p-2 bg-amber-50 text-xs text-amber-800 border border-amber-200 rounded">
                      <span>§ 13b UStG — Reverse-Charge aktiv</span>
                      <span className="font-medium">keine USt</span>
                    </div>
                  ) : (
                    <div className="flex justify-between p-2 bg-gray-50 text-sm">
                      <span>MwSt. ({taxRate}%):</span>
                      <span>{formatCurrency(taxAmount)} EUR</span>
                    </div>
                  )}
                  <div className="flex justify-between p-2 bg-[#9B1B30] text-white font-bold">
                    <span>{reverseCharge ? 'Gesamt (netto):' : 'Gesamt:'}</span>
                    <span>{formatCurrency(grossTotal)} EUR</span>
                  </div>
                  {/* Anzahlung / Deposit */}
                  {(() => {
                    const effectiveDeposit = depositMode === 'percent' 
                      ? (parseFloat(depositPercent) > 0 ? grossTotal * parseFloat(depositPercent) / 100 : 0)
                      : (parseFloat(depositAmount) || 0);
                    return effectiveDeposit > 0 && (
                      <>
                        <div className="flex justify-between p-2 bg-gray-50 text-sm">
                          <span>{documentType === 'abschlag' ? 'Abschlagsbetrag jetzt fällig:' : 'Anzahlung:'}</span>
                          <span data-testid="deposit-line">{formatCurrency(effectiveDeposit)} EUR</span>
                        </div>
                        <div className="flex justify-between p-2 bg-[#9B1B30]/10 text-[#9B1B30] font-bold text-sm" data-testid="restbetrag-line">
                          <span>{documentType === 'abschlag' ? 'Restbetrag (mit Schlussrechnung):' : 'Restbetrag:'}</span>
                          <span>{formatCurrency(grossTotal - effectiveDeposit)} EUR</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Bezug: Angebotsnummer (nur für Abschlagsrechnung) */}
              {documentType === 'abschlag' && (
                <div className="mt-4 pt-4 border-t">
                  <Label className="text-sm font-medium mb-2 block">Bezug: Angebotsnummer (empfohlen)</Label>
                  <Input
                    value={referenceQuoteNumber}
                    onChange={(e) => setReferenceQuoteNumber(e.target.value)}
                    placeholder="z.B. AN-2026-00042"
                    data-testid="input-reference-quote-number"
                  />
                  <p className="text-xs text-gray-500 mt-1">Wird als Bezug auf der Abschlagsrechnung angezeigt</p>
                </div>
              )}

              {/* Anzahlung Input */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">
                    {documentType === 'abschlag' ? 'Abschlagsbetrag (erforderlich)' :
                     documentType === 'angebot' ? 'Anzahlung erforderlich (optional)' : 'Anzahlung geleistet (optional)'}
                  </Label>
                  {/* Toggle EUR / % */}
                  <div className="inline-flex rounded-lg border bg-white text-xs overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setDepositMode('eur')}
                      className={`px-3 py-1 ${depositMode === 'eur' ? 'bg-[#9B1B30] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                      data-testid="deposit-mode-eur"
                    >EUR</button>
                    <button
                      type="button"
                      onClick={() => setDepositMode('percent')}
                      className={`px-3 py-1 border-l ${depositMode === 'percent' ? 'bg-[#9B1B30] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                      data-testid="deposit-mode-percent"
                    >%</button>
                  </div>
                </div>
                {depositMode === 'eur' ? (
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={depositAmount}
                      onChange={e => setDepositAmount(e.target.value)}
                      placeholder={documentType === 'abschlag' ? 'z.B. 500 als Abschlag anfordern...' : documentType === 'angebot' ? 'Anzahlung die der Kunde leisten muss...' : 'Bereits gezahlter Betrag...'}
                      data-testid="input-deposit-amount"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">EUR</span>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={depositPercent}
                      onChange={e => setDepositPercent(e.target.value)}
                      placeholder={documentType === 'abschlag' ? 'z.B. 30 für 30% Abschlag...' : 'z.B. 30 für 30% Anzahlung...'}
                      data-testid="input-deposit-percent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                )}
                {(() => {
                  const dep = depositMode === 'percent' 
                    ? (parseFloat(depositPercent) > 0 ? grossTotal * parseFloat(depositPercent) / 100 : 0)
                    : (parseFloat(depositAmount) || 0);
                  return dep > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {documentType === 'abschlag'
                        ? `Gesamtprojekt: ${formatCurrency(grossTotal)} EUR — Abschlag jetzt: ${formatCurrency(dep)} EUR — Restbetrag (Schlussrechnung): ${formatCurrency(grossTotal - dep)} EUR`
                        : documentType === 'angebot'
                        ? `Gesamtbetrag: ${formatCurrency(grossTotal)} EUR — Anzahlung: ${formatCurrency(dep)} EUR — Restbetrag: ${formatCurrency(grossTotal - dep)} EUR`
                        : `Restbetrag zu zahlen: ${formatCurrency(grossTotal - dep)} EUR`
                      }
                    </p>
                  );
                })()}
              </div>
              
              {/* Note/Remark field */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-medium">Nachbemerkung (optional)</Label>
                  <Select onValueChange={(templateId) => {
                    const template = textTemplates.find(t => t.id === templateId);
                    if (template) {
                      setDocData({...docData, noteText: template.text});
                    }
                  }}>
                    <SelectTrigger className="w-[180px] h-8 text-sm">
                      <SelectValue placeholder="Vorlage wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {textTemplates.filter(t => t.type === 'note').map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea 
                  value={docData.noteText || ''}
                  onChange={(e) => setDocData({...docData, noteText: e.target.value})}
                  placeholder="z.B. Anzahlung erhalten, Besondere Hinweise, Gültigkeit des Angebots..."
                  className="min-h-[60px] mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Erscheint unterhalb der Positionen auf dem Dokument</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Display Options */}
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="p-4 md:p-6">
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-amber-600" />
                Anzeigeoptionen (PDF)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                  <input 
                    type="checkbox" 
                    id="showTaxTable"
                    checked={docData.showTaxTable !== false}
                    onChange={(e) => setDocData({...docData, showTaxTable: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300 text-[#9B1B30] focus:ring-[#9B1B30]"
                  />
                  <label htmlFor="showTaxTable" className="text-sm font-medium cursor-pointer">
                    Steuerdetails anzeigen
                  </label>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                  <input 
                    type="checkbox" 
                    id="showPaymentInfo"
                    checked={docData.showPaymentInfo !== false}
                    onChange={(e) => setDocData({...docData, showPaymentInfo: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300 text-[#9B1B30] focus:ring-[#9B1B30]"
                  />
                  <label htmlFor="showPaymentInfo" className="text-sm font-medium cursor-pointer">
                    Zahlungsinfos anzeigen
                  </label>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                  <input 
                    type="checkbox" 
                    id="showQrCode"
                    checked={docData.showQrCode !== false}
                    onChange={(e) => setDocData({...docData, showQrCode: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300 text-[#9B1B30] focus:ring-[#9B1B30]"
                  />
                  <label htmlFor="showQrCode" className="text-sm font-medium cursor-pointer">
                    QR-Code anzeigen
                  </label>
                </div>
              </div>
              {/* Reverse-Charge Toggle für dieses Dokument */}
              <div className={`mt-4 flex items-start gap-3 p-3 rounded-lg border ${reverseCharge ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
                <input
                  type="checkbox"
                  id="reverseChargeDoc"
                  checked={reverseCharge}
                  onChange={(e) => setReverseCharge(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-[#9B1B30] focus:ring-[#9B1B30] mt-0.5"
                  data-testid="doc-reverse-charge-toggle"
                />
                <div className="flex-1">
                  <label htmlFor="reverseChargeDoc" className="text-sm font-medium cursor-pointer block">
                    Reverse-Charge nach § 13b UStG
                  </label>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Netto-Rechnung ohne USt-Ausweis. Der Pflichtvermerk „Steuerschuldnerschaft des Leistungsempfängers (Reverse-Charge)" wird automatisch auf dem PDF gedruckt.
                  </p>
                  {reverseCharge && (
                    <p className="text-xs text-amber-800 mt-1 font-medium">
                      ⚠ MwSt-Ausweis, Steuerdetails und QR-Code werden unterdrückt.
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-amber-700 mt-3">
                💡 Tipp: Bei Angeboten und Auftragsbestätigungen empfiehlt es sich, Steuerdetails und Zahlungsinfos auszuschalten.
              </p>
            </CardContent>
          </Card>
          
          {/* Tax & Payment */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Steuer</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0">
                <Label className="text-sm">Steuersatz (%)</Label>
                <Input 
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value) || 19)}
                  className="w-24"
                />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Zahlung</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-3">
                <div>
                  <Label className="text-sm">Zahlungsbedingung</Label>
                  <Input 
                    value={payment.terms}
                    onChange={(e) => setPayment({...payment, terms: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-sm">IBAN</Label>
                    <Input 
                      value={payment.iban}
                      onChange={(e) => setPayment({...payment, iban: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">BIC</Label>
                    <Input 
                      value={payment.bic}
                      onChange={(e) => setPayment({...payment, bic: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Bank</Label>
                    <Input 
                      value={payment.bankName}
                      onChange={(e) => setPayment({...payment, bankName: e.target.value})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Footer & Generate */}
          <Card>
            <CardContent className="p-4 md:p-6 pt-4 md:pt-6">
              <div className="mb-4">
                <Label className="text-sm">Fußbereich (optional)</Label>
                <Textarea 
                  value={footerText}
                  onChange={(e) => setFooterText(e.target.value)}
                  placeholder="Zusätzlicher Text..."
                  className="min-h-[60px]"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button 
                  onClick={previewDocument}
                  disabled={isPreviewing}
                  variant="outline"
                  className="flex-1 h-11 md:h-12 text-sm md:text-lg"
                >
                  <Eye className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  {isPreviewing ? 'Lädt...' : 'Vorschau'}
                </Button>
                <Button 
                  onClick={saveDraft}
                  disabled={isSaving}
                  variant="outline"
                  className="flex-1 h-11 md:h-12 text-sm md:text-lg border-amber-500 text-amber-600 hover:bg-amber-50"
                >
                  <Save className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  {isSaving ? 'Speichert...' : 'Zwischenspeichern'}
                </Button>
                <Button 
                  onClick={generatePDF}
                  disabled={isGenerating}
                  className="flex-1 bg-[#9B1B30] hover:bg-[#7a1526] h-11 md:h-12 text-sm md:text-lg"
                  data-testid="generate-pdf-btn"
                >
                  <Download className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  {isGenerating ? 'Wird erstellt...' : 'PDF erstellen'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* ARCHIVE TAB */}
        <TabsContent value="archive">
          {/* DATEV Export Section */}
          <Card className="mb-4 md:mb-6">
            <CardHeader className="p-4 md:p-6">
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 md:w-5 md:h-5" />
                DATEV Export
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              <div className="flex flex-col sm:flex-row sm:items-end gap-3 md:gap-4">
                <div className="flex gap-2 flex-1">
                  <div className="flex-1 sm:flex-none">
                    <Label className="text-sm">Monat</Label>
                    <Select value={String(exportMonth)} onValueChange={(v) => setExportMonth(parseInt(v))}>
                      <SelectTrigger className="w-full sm:w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map(m => (
                          <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-20 sm:w-24">
                    <Label className="text-sm">Jahr</Label>
                    <Select value={String(exportYear)} onValueChange={(v) => setExportYear(parseInt(v))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={exportDATEV} className="bg-[#9B1B30] hover:bg-[#7a1526] w-full sm:w-auto">
                  <Download className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Export {MONTHS[exportMonth-1]?.label} {exportYear}</span>
                  <span className="sm:hidden">Export</span>
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Archive Table */}
          <Card>
            <CardHeader className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <Search className="w-4 h-4 text-gray-400" />
                  <Input 
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1 sm:w-48"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Typen</SelectItem>
                      <SelectItem value="rechnung">Rechnungen</SelectItem>
                      <SelectItem value="angebot">Angebote</SelectItem>
                      <SelectItem value="auftragsbestaetigung">AB</SelectItem>
                      <SelectItem value="abschlag">Abschlagsrechnungen</SelectItem>
                      <SelectItem value="mahnung">Mahnungen</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full sm:w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Status</SelectItem>
                      <SelectItem value="offen">Offen</SelectItem>
                      <SelectItem value="bezahlt">Bezahlt</SelectItem>
                      <SelectItem value="teilbezahlt">Teilbezahlt</SelectItem>
                      <SelectItem value="ueberfaellig">Überfällig</SelectItem>
                      <SelectItem value="gemahnt">Gemahnt</SelectItem>
                      <SelectItem value="storniert">Storniert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              {/* Mobile: Card-Layout für Archiv */}
              <div className="block md:hidden space-y-3">
                {archive.map(doc => {
                  const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.offen;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <div key={doc.id} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          doc.document_type === 'rechnung' ? 'bg-green-100 text-green-700' :
                          doc.document_type === 'angebot' ? 'bg-blue-100 text-blue-700' :
                          doc.document_type === 'abschlag' ? 'bg-[#9B1B30]/10 text-[#9B1B30]' :
                          doc.document_type === 'mahnung' ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`} data-testid={`mobile-doc-type-${doc.id}`}>
                          {doc.document_type === 'rechnung' ? 'Rechnung' : 
                           doc.document_type === 'angebot' ? 'Angebot' : 
                           doc.document_type === 'abschlag' ? 'Abschlag' :
                           doc.document_type === 'mahnung' ? `${doc.dunning_level}. Mahnung` : 'AB'}
                        </span>
                        {doc.document_type === 'rechnung' && (
                          <Select 
                            value={doc.status} 
                            onValueChange={(val) => updateDocumentStatus(doc.id, val)}
                          >
                            <SelectTrigger className="h-7 w-28 text-xs">
                              <StatusIcon className="w-3 h-3 mr-1" />
                              <span className="capitalize">{doc.status}</span>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <SelectItem key={key} value={key}>{config.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="font-mono text-sm font-medium">{doc.document_number}</div>
                      <div className="text-sm text-gray-600">{doc.billing_name || doc.customer_number}</div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500">{doc.document_date}</span>
                        <span className="font-bold text-[#9B1B30]">{formatCurrency(doc.gross_total || 0)} €</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {/* Weiterführen - nur für Angebote (Mobile) */}
                        {doc.document_type === 'angebot' && (
                          <>
                            <Button variant="outline" size="sm" className="h-8 text-xs text-blue-600" onClick={() => convertDocument(doc, 'rechnung')}>
                              <ArrowRight className="w-3 h-3 mr-1" /> → RE
                            </Button>
                            <Button variant="outline" size="sm" className="h-8 text-xs text-[#9B1B30]" onClick={() => convertDocument(doc, 'abschlag')} data-testid={`mobile-to-abschlag-${doc.id}`}>
                              <ArrowRight className="w-3 h-3 mr-1" /> → AR
                            </Button>
                          </>
                        )}
                        {doc.document_type === 'auftragsbestaetigung' && (
                          <Button variant="outline" size="sm" className="h-8 text-xs text-blue-600" onClick={() => convertDocument(doc, 'rechnung')} data-testid={`mobile-ab-to-rechnung-${doc.id}`}>
                            <ArrowRight className="w-3 h-3 mr-1" /> → RE
                          </Button>
                        )}
                        {/* Zu Gutachten umwandeln (Mobile) */}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8 text-xs text-purple-600"
                          onClick={() => convertToGutachten(doc)}
                          disabled={convertingToGutachten === doc.id}
                          data-testid={`mobile-to-gutachten-${doc.id}`}
                        >
                          {convertingToGutachten === doc.id ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Scale className="w-3 h-3 mr-1" />
                          )}
                          Gutachten
                        </Button>
                        {doc.document_type !== 'mahnung' && (
                          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => editDocument(doc)}>
                            <Edit2 className="w-3 h-3 mr-1" /> Bearbeiten
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="h-8 text-xs text-[#9B1B30] border-[#9B1B30]/30" onClick={() => openRecipientDialog(doc)} data-testid={`archive-edit-recipient-mobile-${doc.id}`}>
                          <Edit2 className="w-3 h-3 mr-1" /> Empfänger
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 text-xs text-blue-600 border-blue-200" onClick={() => regeneratePdf(doc)} disabled={regeneratingDocId === doc.id} data-testid={`archive-regenerate-mobile-${doc.id}`}>
                          {regeneratingDocId === doc.id ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3 h-3 mr-1" />
                          )}
                          Aktualisieren
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => downloadArchiveDoc(doc)}>
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => sendDocumentEmail(doc)} data-testid={`archive-send-email-btn-mobile-${doc.id}`}>
                          <Send className="w-3 h-3 mr-1" /> E-Mail
                        </Button>
                        {canCreateDunning(doc) && (
                          <Button variant="outline" size="sm" className="h-8 text-xs text-red-700 border-red-200" onClick={() => openDunningDialog(doc)} data-testid={`archive-create-dunning-mobile-${doc.id}`}>
                            <FileWarning className="w-3 h-3 mr-1" /> {nextDunningLabel(doc)}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Desktop: Tabellen-Layout für Archiv */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-sm">Typ</TableHead>
                      <TableHead className="text-sm">Nummer</TableHead>
                      <TableHead className="text-sm">Kunde</TableHead>
                      <TableHead className="text-sm">Datum</TableHead>
                      <TableHead className="text-right text-sm">Betrag</TableHead>
                      <TableHead className="text-sm">Status</TableHead>
                      <TableHead className="text-sm">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archive.map(doc => {
                      const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.offen;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <TableRow key={doc.id}>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              doc.document_type === 'rechnung' ? 'bg-green-100 text-green-700' :
                              doc.document_type === 'angebot' ? 'bg-blue-100 text-blue-700' :
                              doc.document_type === 'abschlag' ? 'bg-[#9B1B30]/10 text-[#9B1B30]' :
                              doc.document_type === 'mahnung' ? 'bg-red-100 text-red-700' :
                              'bg-orange-100 text-orange-700'
                            }`} data-testid={`doc-type-${doc.id}`} title={doc.document_type === 'mahnung' ? `${doc.dunning_level}. Mahnung zu ${doc.reference_invoice_number}` : undefined}>
                              {doc.document_type === 'rechnung' ? 'RE' : 
                               doc.document_type === 'angebot' ? 'AN' : 
                               doc.document_type === 'abschlag' ? 'AR' :
                               doc.document_type === 'mahnung' ? `MA${doc.dunning_level}` : 'AB'}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{doc.document_number}</TableCell>
                          <TableCell className="text-sm">{doc.billing_name || doc.customer_number}</TableCell>
                          <TableCell className="text-sm">{doc.document_date}</TableCell>
                          <TableCell className="text-right font-medium text-sm">
                            {formatCurrency(doc.gross_total || 0)} €
                          </TableCell>
                          <TableCell>
                            {doc.document_type === 'rechnung' ? (
                            <Select 
                              value={doc.status || 'offen'} 
                              onValueChange={(val) => updateDocumentStatus(doc.id, val)}
                            >
                              <SelectTrigger className={`w-32 h-8 ${statusConfig.color}`}>
                                <div className="flex items-center gap-1">
                                  <StatusIcon className="w-3 h-3" />
                                  <span className="text-xs">{statusConfig.label}</span>
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="offen">
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-3 h-3" /> Offen
                                  </div>
                                </SelectItem>
                                <SelectItem value="bezahlt">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="w-3 h-3" /> Bezahlt
                                  </div>
                                </SelectItem>
                                <SelectItem value="teilbezahlt">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle className="w-3 h-3" /> Teilbezahlt
                                  </div>
                                </SelectItem>
                                <SelectItem value="ueberfaellig">
                                  <div className="flex items-center gap-2">
                                    <AlertCircle className="w-3 h-3" /> Überfällig
                                  </div>
                                </SelectItem>
                                <SelectItem value="gemahnt">
                                  <div className="flex items-center gap-2">
                                    <AlertCircle className="w-3 h-3" /> Gemahnt
                                  </div>
                                </SelectItem>
                                <SelectItem value="storniert">
                                  <div className="flex items-center gap-2">
                                    <XCircle className="w-3 h-3" /> Storniert
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : doc.document_type === 'mahnung' ? (
                            <span className="text-xs text-gray-500" data-testid={`dunning-ref-${doc.id}`}>zu {doc.reference_invoice_number}</span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {/* Weiterführen - nur für Angebote */}
                            {doc.document_type === 'angebot' && (
                              <div className="relative group">
                                <Button variant="ghost" size="sm" className="text-blue-600" title="Weiterführen als...">
                                  <ArrowRight className="w-4 h-4" />
                                </Button>
                                <div className="absolute right-0 top-8 hidden group-hover:block bg-white border rounded-lg shadow-lg z-50 min-w-[180px]">
                                  <button
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                    onClick={() => convertDocument(doc, 'rechnung')}
                                  >
                                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">RE</span>
                                    Als Rechnung
                                  </button>
                                  <button
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                    onClick={() => convertDocument(doc, 'auftragsbestaetigung')}
                                  >
                                    <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">AB</span>
                                    Als Auftragsbestätigung
                                  </button>
                                  <button
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                    onClick={() => convertDocument(doc, 'abschlag')}
                                    data-testid={`desktop-to-abschlag-${doc.id}`}
                                  >
                                    <span className="px-1.5 py-0.5 bg-[#9B1B30]/10 text-[#9B1B30] rounded text-xs">AR</span>
                                    Als Abschlagsrechnung
                                  </button>
                                </div>
                              </div>
                            )}
                            {/* Auftragsbestätigung → Rechnung */}
                            {doc.document_type === 'auftragsbestaetigung' && (
                              <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => convertDocument(doc, 'rechnung')} title="Als Rechnung weiterführen" data-testid={`desktop-ab-to-rechnung-${doc.id}`}>
                                <ArrowRight className="w-4 h-4" />
                                <span className="ml-1 text-xs hidden lg:inline">RE</span>
                              </Button>
                            )}
                            {/* Bearbeiten */}
                            {doc.document_type !== 'mahnung' && (
                              <Button variant="ghost" size="sm" onClick={() => editDocument(doc)} title="Bearbeiten">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Empfänger / Lieferadresse ändern */}
                            <Button variant="ghost" size="sm" onClick={() => openRecipientDialog(doc)} title="Empfänger / Lieferadresse ändern" className="text-[#9B1B30] hover:bg-[#9B1B30]/10" data-testid={`archive-edit-recipient-${doc.id}`}>
                              <Edit2 className="w-4 h-4" />
                              <span className="ml-1 text-xs hidden lg:inline">Empfänger</span>
                            </Button>
                            {/* PDF neu erzeugen */}
                            <Button variant="ghost" size="sm" onClick={() => regeneratePdf(doc)} title="PDF neu erzeugen (fehlende Abschnitte einfügen)" disabled={regeneratingDocId === doc.id} className="text-blue-600 hover:bg-blue-50" data-testid={`archive-regenerate-${doc.id}`}>
                              {regeneratingDocId === doc.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                              <span className="ml-1 text-xs hidden lg:inline">Aktualisieren</span>
                            </Button>
                            {/* Zu Gutachten umwandeln */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => convertToGutachten(doc)} 
                              title="Zu Gutachten umwandeln"
                              className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                              disabled={convertingToGutachten === doc.id}
                              data-testid={`desktop-to-gutachten-${doc.id}`}
                            >
                              {convertingToGutachten === doc.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Scale className="w-4 h-4" />
                              )}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => downloadArchiveDoc(doc)} title="PDF herunterladen">
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => sendDocumentEmail(doc)} title="Per E-Mail senden" data-testid={`archive-send-email-btn-${doc.id}`}>
                              <Send className="w-4 h-4" />
                            </Button>
                            {/* Mahnung erstellen (Rechnung → 1. → 2. → 3. Mahnung) */}
                            {canCreateDunning(doc) && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openDunningDialog(doc)} 
                                className="text-red-700 hover:text-red-800 hover:bg-red-50"
                                title={`${nextDunningLabel(doc)} erstellen`}
                                data-testid={`archive-create-dunning-${doc.id}`}
                              >
                                <FileWarning className="w-4 h-4" />
                                <span className="ml-1 text-xs hidden lg:inline">{nextDunningLabel(doc)}</span>
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => deleteDocument(doc)} className="text-red-500" title="Löschen">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {archive.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                        Keine Dokumente gefunden
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
              
              {archive.length === 0 && (
                <div className="block md:hidden text-center text-gray-500 py-8">
                  Keine Dokumente gefunden
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* SETTINGS TAB */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Number Settings */}
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Nummernkreise</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-4">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label className="text-sm">Rechnungs-Präfix</Label>
                    <Input 
                      value={settings.invoice_prefix || 'RE-'}
                      onChange={(e) => setSettings({...settings, invoice_prefix: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Nächste Nr.</Label>
                    <Input 
                      type="number"
                      value={settings.invoice_next_number || 1}
                      onChange={(e) => setSettings({...settings, invoice_next_number: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label className="text-sm">Angebots-Präfix</Label>
                    <Input 
                      value={settings.quote_prefix || 'AN-'}
                      onChange={(e) => setSettings({...settings, quote_prefix: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Nächste Nr.</Label>
                    <Input 
                      type="number"
                      value={settings.quote_next_number || 1}
                      onChange={(e) => setSettings({...settings, quote_next_number: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <Label className="text-sm">Brief-Präfix</Label>
                    <Input 
                      value={settings.letter_prefix ?? 'BR-'}
                      onChange={(e) => setSettings({...settings, letter_prefix: e.target.value})}
                      data-testid="letter-prefix-setting"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Nächste Brief-Nr.</Label>
                    <Input 
                      type="number"
                      value={settings.letter_next_number || 333}
                      onChange={(e) => setSettings({...settings, letter_next_number: parseInt(e.target.value)})}
                      data-testid="letter-next-number-setting"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>AB-Präfix</Label>
                    <Input 
                      value={settings.order_prefix || 'AB-'}
                      onChange={(e) => setSettings({...settings, order_prefix: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Nächste Nr.</Label>
                    <Input 
                      type="number"
                      value={settings.order_next_number || 1}
                      onChange={(e) => setSettings({...settings, order_next_number: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Nummernformat</Label>
                  <Input 
                    value={settings.number_format || '{prefix}{year}-{number:05d}'}
                    onChange={(e) => setSettings({...settings, number_format: e.target.value})}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Variablen: {'{prefix}'}, {'{year}'}, {'{number:05d}'}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Logo Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Firmenlogo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {settings.logo_url && (
                  <div className="border rounded p-4 text-center bg-gray-50">
                    <img 
                      src={settings.logo_url} 
                      alt="Logo" 
                      style={{ width: settings.logo_width || 150, maxWidth: '100%' }}
                      className="mx-auto"
                    />
                    <p className="text-xs text-green-600 mt-2">✓ Logo gespeichert</p>
                  </div>
                )}
                <div>
                  <Label>Logo hochladen</Label>
                  <Input 
                    type="file"
                    accept="image/*"
                    onChange={uploadLogo}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Position</Label>
                  <Select 
                    value={settings.logo_position || 'top-right'}
                    onValueChange={(val) => setSettings({...settings, logo_position: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top-left">Oben links</SelectItem>
                      <SelectItem value="top-center">Oben mittig</SelectItem>
                      <SelectItem value="top-right">Oben rechts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Logo-Breite (px)</Label>
                  <div className="flex items-center gap-3">
                    <Input 
                      type="range"
                      min="50"
                      max="800"
                      value={settings.logo_width || 150}
                      onChange={(e) => setSettings({...settings, logo_width: parseInt(e.target.value)})}
                      className="flex-1"
                    />
                    <Input 
                      type="number"
                      min="50"
                      max="800"
                      value={settings.logo_width || 150}
                      onChange={(e) => setSettings({...settings, logo_width: parseInt(e.target.value) || 150})}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-500">px</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Layout Selection */}
            <Card className="lg:col-span-2">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">PDF-Layout auswählen</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0">
                <p className="text-xs md:text-sm text-gray-500 mb-3 md:mb-4">Alle Layouts verwenden Ihre Firmenfarbe.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
                  {[
                    { id: 1, name: 'Klassisch', desc: 'Rahmen', preview: 'boxed' },
                    { id: 2, name: 'Modern', desc: 'Linien', preview: 'lines' },
                    { id: 3, name: 'Minimal', desc: 'Weißraum', preview: 'minimal' },
                    { id: 4, name: 'Professional', desc: 'Grid', preview: 'grid' },
                    { id: 5, name: 'Elegant', desc: 'Verlauf', preview: 'gradient' },
                    { id: 6, name: 'Bold', desc: 'Kontrast', preview: 'bold' },
                    { id: 7, name: 'Clean', desc: 'Kein Rahmen', preview: 'clean' },
                    { id: 8, name: 'Corporate', desc: 'Header', preview: 'corporate' },
                    { id: 9, name: 'Simple', desc: 'Einfach', preview: 'simple' },
                    { id: 10, name: 'Premium', desc: 'Doppel', preview: 'premium' },
                  ].map(layout => (
                    <div
                      key={layout.id}
                      onClick={() => setSettings({...settings, selected_layout: layout.id})}
                      className={`cursor-pointer rounded-lg border-2 p-2 md:p-3 transition-all ${
                        (settings.selected_layout || 1) === layout.id 
                          ? 'border-[#9B1B30] shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {/* Mini Preview - verschiedene Stile, gleiche Farbe */}
                      <div 
                        className="h-16 md:h-24 rounded mb-1 md:mb-2 flex flex-col text-[5px] md:text-[6px] overflow-hidden"
                        style={{ background: '#fff', border: '1px solid #eee' }}
                      >
                        {/* Header - unterschiedliche Stile */}
                        {layout.preview === 'corporate' ? (
                          <div style={{ background: '#9B1B30', height: '12px' }}></div>
                        ) : layout.preview === 'bold' ? (
                          <div style={{ background: '#1f2937', height: '10px' }}></div>
                        ) : layout.preview === 'premium' ? (
                          <div style={{ borderBottom: '2px double #9B1B30', height: '8px' }}></div>
                        ) : layout.preview === 'lines' ? (
                          <div style={{ borderBottom: '3px solid #9B1B30', height: '8px' }}></div>
                        ) : (
                          <div style={{ height: '6px' }}></div>
                        )}
                        {/* Title */}
                        <div className="px-1 pt-1">
                          <div style={{ color: '#9B1B30', fontWeight: 'bold', fontSize: '5px' }}>RECHNUNG</div>
                        </div>
                        {/* Content - unterschiedliche Stile */}
                        <div className="px-1 space-y-0.5 flex-1">
                          {layout.preview === 'boxed' || layout.preview === 'grid' ? (
                            <div className="flex gap-1">
                              <div className="h-3 w-10 border border-gray-200 rounded-sm bg-gray-50"></div>
                              <div className="h-3 w-10 border border-gray-200 rounded-sm bg-gray-50"></div>
                            </div>
                          ) : layout.preview === 'minimal' || layout.preview === 'clean' ? (
                            <div className="flex gap-2 mt-1">
                              <div className="h-2 w-12 bg-gray-50"></div>
                              <div className="h-2 w-12 bg-gray-50"></div>
                            </div>
                          ) : (
                            <div className="flex gap-1">
                              <div className="h-2 w-8 bg-gray-100 rounded-sm"></div>
                              <div className="h-2 w-8 bg-gray-100 rounded-sm"></div>
                            </div>
                          )}
                          <div className="h-1.5 w-full" style={{ background: '#9B1B30' }}></div>
                          <div className="h-1 w-full bg-gray-50"></div>
                          {layout.preview !== 'simple' && <div className="h-1 w-full bg-gray-50"></div>}
                        </div>
                        {/* Footer */}
                        <div style={{ borderTop: '1px solid #9B1B30', height: '6px', background: '#f9f9f9' }}></div>
                      </div>
                      {/* Layout Name */}
                      <div className="text-center">
                        <div className="font-medium text-xs md:text-sm text-[#9B1B30]">{layout.name}</div>
                        <div className="text-[10px] md:text-xs text-gray-500">{layout.desc}</div>
                      </div>
                      {/* Selection Indicator */}
                      {(settings.selected_layout || 1) === layout.id && (
                        <div className="text-center mt-1">
                          <span className="text-[10px] md:text-xs bg-[#9B1B30] text-white px-1.5 md:px-2 py-0.5 rounded-full">✓</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Header Settings */}
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Header-Einstellungen</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <Label className="text-sm">Firmeninfo im Header</Label>
                    <p className="text-xs text-gray-500 hidden sm:block">Firmenname, Adresse im Kopfbereich</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.show_header_company_info !== false}
                      onChange={(e) => setSettings({...settings, show_header_company_info: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-[#9B1B30] after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <Label className="text-sm">Absenderzeile</Label>
                    <p className="text-xs text-gray-500 hidden sm:block">Kleine Absenderzeile über Empfängeradresse</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.header_sender_line !== false}
                      onChange={(e) => setSettings({...settings, header_sender_line: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-[#9B1B30] after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>
              </CardContent>
            </Card>
            
            {/* Reverse-Charge § 13b UStG (globales Default) */}
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Umsatzsteuer / Reverse-Charge</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <Label className="text-sm font-medium">Reverse-Charge § 13b UStG als Standard aktivieren</Label>
                    <p className="text-xs text-gray-500">Neue Rechnungen werden dann automatisch <strong>ohne Umsatzsteuer</strong> ausgestellt und tragen den Pflichtvermerk „Steuerschuldnerschaft des Leistungsempfängers".</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.reverse_charge === true}
                      onChange={(e) => setSettings({...settings, reverse_charge: e.target.checked})}
                      className="sr-only peer"
                      data-testid="toggle-reverse-charge"
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-[#9B1B30] after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>
                {settings.reverse_charge === true && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900">
                    <strong>Aktiv:</strong> Neue Dokumente werden standardmäßig als Reverse-Charge-Rechnung erstellt. Sie können das pro Dokument im Reiter „Erstellen" wieder ausschalten.
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Mahnwesen */}
            <Card data-testid="dunning-settings-card">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Mahnwesen</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Präfix Mahnungsnummer</Label>
                    <Input value={settings.dunning_prefix ?? ''} onChange={(e) => setSettings({ ...settings, dunning_prefix: e.target.value })} data-testid="dunning-prefix-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Zahlungsfrist (Tage, alle Stufen)</Label>
                    <Input type="number" min="0" value={settings.dunning_payment_days ?? ''} onChange={(e) => setSettings({ ...settings, dunning_payment_days: parseInt(e.target.value, 10) || 0 })} data-testid="dunning-payment-days-setting" />
                  </div>
                  <div>
                    <Label className="text-xs">Basiszinssatz (%)</Label>
                    <Input type="number" step="0.01" value={settings.dunning_base_rate ?? ''} onChange={(e) => setSettings({ ...settings, dunning_base_rate: parseFloat(e.target.value) })} data-testid="dunning-base-rate-setting" />
                  </div>
                  <div>
                    <Label className="text-xs">Aufschlag Privat / Firma (%-Punkte)</Label>
                    <div className="flex gap-2">
                      <Input type="number" step="0.5" value={settings.dunning_rate_b2c ?? ''} onChange={(e) => setSettings({ ...settings, dunning_rate_b2c: parseFloat(e.target.value) })} data-testid="dunning-rate-b2c-setting" />
                      <Input type="number" step="0.5" value={settings.dunning_rate_b2b ?? ''} onChange={(e) => setSettings({ ...settings, dunning_rate_b2b: parseFloat(e.target.value) })} data-testid="dunning-rate-b2b-setting" />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Verzugszinsen (§ 288 BGB) werden nur auf der 3. Mahnung berechnet – ab Fälligkeit der Rechnung. Der Basiszinssatz wird halbjährlich von der Bundesbank festgelegt (1.1. / 1.7.).</p>
                <div>
                  <Label className="text-xs">Hinweis auf 1. und 2. Mahnung (Ankündigung Verzugszinsen)</Label>
                  <Textarea value={settings.dunning_interest_notice ?? ''} onChange={(e) => setSettings({ ...settings, dunning_interest_notice: e.target.value })} className="min-h-[80px] text-sm" data-testid="dunning-interest-notice-setting" />
                </div>
                <div>
                  <Label className="text-xs">Inkasso-Hinweis auf 3. Mahnung</Label>
                  <Textarea value={settings.dunning_inkasso_text ?? ''} onChange={(e) => setSettings({ ...settings, dunning_inkasso_text: e.target.value })} className="min-h-[100px] text-sm" data-testid="dunning-inkasso-text-setting" />
                </div>
                <p className="text-[11px] text-gray-500">Platzhalter: {'{rechnungsnummer}'}, {'{rechnungsdatum}'}, {'{faellig_seit}'}, {'{frist}'}, {'{zinssatz}'}, {'{zinsbasis}'}</p>
              </CardContent>
            </Card>

            {/* Bank Notice Settings */}
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Bankverbindungs-Hinweis</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <Label className="text-sm font-medium">Hinweis auf Dokumenten anzeigen</Label>
                    <p className="text-xs text-gray-500">"Neue Bankverbindung! Bitte beachten Sie unsere geänderten Kontodaten."</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.show_bank_notice !== false}
                      onChange={(e) => setSettings({...settings, show_bank_notice: e.target.checked})}
                      className="sr-only peer"
                      data-testid="toggle-bank-notice"
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-[#9B1B30] after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>
                {settings.show_bank_notice !== false && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-center">
                    <span className="text-yellow-800 font-medium text-sm">&#9888; Neue Bankverbindung! Bitte beachten Sie unsere geänderten Kontodaten.</span>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Company Settings */}
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Firmendaten</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-3">
                <div>
                  <Label className="text-sm">Firmenname</Label>
                  <Input 
                    value={settings.company_name || ''}
                    onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm">Geschäftsführer / Inhaber</Label>
                  <Input 
                    value={settings.company_owner || ''}
                    onChange={(e) => setSettings({...settings, company_owner: e.target.value})}
                    placeholder="z.B. Max Mustermann"
                  />
                </div>
                <div>
                  <Label className="text-sm">Straße</Label>
                  <Input 
                    value={settings.company_street || ''}
                    onChange={(e) => setSettings({...settings, company_street: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm">PLZ / Ort</Label>
                  <Input 
                    value={settings.company_city || ''}
                    onChange={(e) => setSettings({...settings, company_city: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">USt-IdNr.</Label>
                    <Input 
                      value={settings.company_vat_id || ''}
                      onChange={(e) => setSettings({...settings, company_vat_id: e.target.value})}
                      placeholder="DE123456789"
                      data-testid="settings-vat-id"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Steuernummer</Label>
                    <Input 
                      value={settings.company_tax_number || ''}
                      onChange={(e) => setSettings({...settings, company_tax_number: e.target.value})}
                      placeholder="z.B. 123/456/78901"
                      data-testid="settings-tax-number"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">Geschäftsführer</Label>
                    <Input
                      value={settings.company_owner || ''}
                      onChange={(e) => setSettings({...settings, company_owner: e.target.value})}
                      placeholder="Max Mustermann"
                      data-testid="settings-owner"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Handelsregister-Nr. (HRB)</Label>
                    <Input
                      value={settings.company_hrb || ''}
                      onChange={(e) => setSettings({...settings, company_hrb: e.target.value})}
                      placeholder="z.B. 12345"
                      data-testid="settings-hrb"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Amtsgericht / Registergericht</Label>
                    <Input
                      value={settings.company_registration_court || ''}
                      onChange={(e) => setSettings({...settings, company_registration_court: e.target.value})}
                      placeholder="z.B. Neuss"
                      data-testid="settings-registration-court"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">Telefon (Festnetz)</Label>
                    <Input 
                      value={settings.company_phone || ''}
                      onChange={(e) => setSettings({...settings, company_phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Mobilnummer</Label>
                    <Input 
                      value={settings.company_mobile || ''}
                      onChange={(e) => setSettings({...settings, company_mobile: e.target.value})}
                      placeholder="z.B. 0170 1234567"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">E-Mail</Label>
                    <Input 
                      value={settings.company_email || ''}
                      onChange={(e) => setSettings({...settings, company_email: e.target.value})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Payment Defaults */}
            <Card>
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-base md:text-lg">Standard-Zahlungsdaten</CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-3">
                <div>
                  <Label className="text-sm">Zahlungsbedingung</Label>
                  <Input 
                    value={settings.default_payment_terms || ''}
                    onChange={(e) => setSettings({...settings, default_payment_terms: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm">IBAN</Label>
                  <Input 
                    value={settings.default_iban || ''}
                    onChange={(e) => setSettings({...settings, default_iban: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">BIC</Label>
                    <Input 
                      value={settings.default_bic || ''}
                      onChange={(e) => setSettings({...settings, default_bic: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Bankname</Label>
                    <Input 
                      value={settings.default_bank_name || ''}
                      onChange={(e) => setSettings({...settings, default_bank_name: e.target.value})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-4 md:mt-6">
            <Button onClick={saveSettings} className="bg-[#9B1B30] hover:bg-[#7a1526] w-full sm:w-auto">
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen speichern
            </Button>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[95vw] md:max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
              <Eye className="w-4 h-4 md:w-5 md:h-5" />
              Dokumentvorschau
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(90vh-150px)] md:max-h-[calc(90vh-100px)] border rounded">
            <iframe
              srcDoc={previewHtml}
              className="w-full min-h-[400px] md:min-h-[600px]"
              title="Dokumentvorschau"
            />
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 mt-3 md:mt-4">
            <Button variant="outline" onClick={() => setShowPreview(false)} className="w-full sm:w-auto">
              Schließen
            </Button>
            <Button 
              onClick={() => {
                setShowPreview(false);
                generatePDF();
              }}
              className="bg-[#9B1B30] hover:bg-[#7a1526] w-full sm:w-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog with optional Scheduling */}
      <Dialog
        open={emailDialog.open}
        onOpenChange={(open) => !emailDialog.sending && setEmailDialog((p) => ({ ...p, open }))}
      >
        <DialogContent className="max-w-md" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Send className="w-4 h-4" /> Dokument per E-Mail senden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {emailDialog.doc && (
              <div className="text-xs text-gray-500">
                {emailDialog.doc.document_type ? String(emailDialog.doc.document_type).toUpperCase() : 'Dokument'}
                {emailDialog.doc.document_number ? ` – ${emailDialog.doc.document_number}` : ''}
              </div>
            )}
            <div>
              <Label className="text-xs">Empfänger-E-Mail *</Label>
              <Input
                type="email"
                value={emailDialog.email}
                onChange={(e) => setEmailDialog((p) => ({ ...p, email: e.target.value }))}
                placeholder="kunde@beispiel.de"
                className="mt-1"
                data-testid="send-email-recipient-input"
              />
            </div>
            <label className="flex items-center gap-2 select-none pt-1">
              <input
                type="checkbox"
                checked={emailDialog.schedule}
                onChange={(e) =>
                  setEmailDialog((p) => ({
                    ...p,
                    schedule: e.target.checked,
                    scheduledAt: e.target.checked ? p.scheduledAt : '',
                  }))
                }
                data-testid="send-email-schedule-toggle"
              />
              <span className="text-sm">Senden planen (zu einem späteren Zeitpunkt)</span>
            </label>
            {emailDialog.schedule && (
              <div>
                <Label className="text-xs">Datum & Uhrzeit *</Label>
                <Input
                  type="datetime-local"
                  value={emailDialog.scheduledAt}
                  onChange={(e) => setEmailDialog((p) => ({ ...p, scheduledAt: e.target.value }))}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  className="mt-1"
                  data-testid="send-email-scheduled-input"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Die E-Mail wird automatisch zum gewählten Zeitpunkt versendet.
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setEmailDialog({ open: false, doc: null, email: '', schedule: false, scheduledAt: '', sending: false })}
              disabled={emailDialog.sending}
              data-testid="send-email-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={confirmSendDocumentEmail}
              disabled={emailDialog.sending}
              className="bg-[#9B1B30] hover:bg-[#7a1526]"
              data-testid="send-email-confirm-btn"
            >
              {emailDialog.sending
                ? 'Wird gesendet…'
                : emailDialog.schedule
                  ? 'Versand planen'
                  : 'Jetzt senden'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Neues Konto (Document Profile) Dialog */}
      <Dialog
        open={newProfileDialog.open}
        onOpenChange={(open) => !newProfileDialog.saving && setNewProfileDialog((p) => ({ ...p, open }))}
      >
        <DialogContent className="max-w-md" data-testid="new-profile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Plus className="w-4 h-4" /> Neues Rechnungskonto anlegen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Firmen-/Kontoname *</Label>
              <Input
                value={newProfileDialog.name}
                onChange={(e) => setNewProfileDialog((p) => ({ ...p, name: e.target.value }))}
                placeholder="z. B. FarbWelt Maler Express Service UG"
                className="mt-1"
                data-testid="new-profile-name"
              />
            </div>
            <div>
              <Label className="text-xs">Nummernkürzel (optional, z. B. „FW")</Label>
              <Input
                value={newProfileDialog.prefix_suffix}
                onChange={(e) => setNewProfileDialog((p) => ({ ...p, prefix_suffix: e.target.value.toUpperCase() }))}
                maxLength={5}
                placeholder="FW"
                className="mt-1"
                data-testid="new-profile-suffix"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Nummer sieht dann so aus: RE-2026-<strong>{newProfileDialog.prefix_suffix || '…'}</strong>00001
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Start Rechnung</Label>
                <Input
                  type="number" min={1}
                  value={newProfileDialog.invoice_next_number}
                  onChange={(e) => setNewProfileDialog((p) => ({ ...p, invoice_next_number: e.target.value }))}
                  className="mt-1"
                  data-testid="new-profile-invoice-start"
                />
              </div>
              <div>
                <Label className="text-xs">Start Angebot</Label>
                <Input
                  type="number" min={1}
                  value={newProfileDialog.quote_next_number}
                  onChange={(e) => setNewProfileDialog((p) => ({ ...p, quote_next_number: e.target.value }))}
                  className="mt-1"
                  data-testid="new-profile-quote-start"
                />
              </div>
              <div>
                <Label className="text-xs">Start AB</Label>
                <Input
                  type="number" min={1}
                  value={newProfileDialog.order_next_number}
                  onChange={(e) => setNewProfileDialog((p) => ({ ...p, order_next_number: e.target.value }))}
                  className="mt-1"
                  data-testid="new-profile-order-start"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setNewProfileDialog({ open: false, name: '', prefix_suffix: '', invoice_next_number: 1, quote_next_number: 1, order_next_number: 1, saving: false })}
              disabled={newProfileDialog.saving}
            >
              Abbrechen
            </Button>
            <Button
              onClick={async () => {
                const name = (newProfileDialog.name || '').trim();
                if (!name) { toast.error('Bitte einen Kontonamen eingeben'); return; }
                setNewProfileDialog((p) => ({ ...p, saving: true }));
                try {
                  const res = await api.post('/admin/document-profiles', {
                    name,
                    prefix_suffix: newProfileDialog.prefix_suffix || '',
                    invoice_next_number: parseInt(newProfileDialog.invoice_next_number) || 1,
                    quote_next_number: parseInt(newProfileDialog.quote_next_number) || 1,
                    order_next_number: parseInt(newProfileDialog.order_next_number) || 1,
                  });
                  toast.success('Konto angelegt');
                  await loadProfiles();
                  setActiveProfile(res.data?.profile?.id || 'default');
                  setNewProfileDialog({ open: false, name: '', prefix_suffix: '', invoice_next_number: 1, quote_next_number: 1, order_next_number: 1, saving: false });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Anlegen fehlgeschlagen');
                  setNewProfileDialog((p) => ({ ...p, saving: false }));
                }
              }}
              disabled={newProfileDialog.saving}
              className="bg-[#9B1B30] hover:bg-[#7a1526]"
              data-testid="new-profile-save"
            >
              {newProfileDialog.saving ? 'Speichere…' : 'Konto anlegen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
